import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  getOrders, getOrder, getOrderStatusCounts, getOrderBalance, createOrder, updateOrder, deleteOrder, updateClient, bulkOrdersAction,
  addCarToOrder, getCar, updateCar, deleteCar,
  getClients, getSuppliers, getShippingCompanies,
  getClientBeneficiaries, uploadFile, getStockVehicles,
  getBrands, getModels,
  getOrderPO, getOrderPOPdfUrl, getDeliveryNotePdfUrl, updateOrderStatus,
  addPayment, getOrderPayments, deletePayment,
  sendAvisEmail, getEmployees,
  createTrackingRequest, getOrderTracking, getTrackingStatus, getScacCodes,
  getOrderImportTemplateUrl, previewOrdersImport, confirmOrdersImport, getNewOrdersSummary, getOrderTimeline,
  aiPreviewOrdersImport, getAiImportStatus,
  getPorts,
  findDuplicateOrders, deleteOrderByMongoId
} from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import CustomFeesEditor from './CustomFeesEditor';
import MoneyInput from '../../components/MoneyInput';
import ClientPicker from '../../components/ClientPicker';
import StatusPills from './StatusPills';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription
} from '../../components/ui/sheet';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Switch } from '../../components/ui/switch';
import { toast } from 'sonner';
import SignatureCanvas from 'react-signature-canvas';

const ORDER_STATUSES = [
  'new_order', 'order_submitted', 'ready_to_load', 'loaded',
  'shipped', 'arrived_alg', 'cleared_port', 'delivered', 'cancelled'
];

const ORDER_STATUS_LABELS = {
  new_order: 'New Order', order_submitted: 'Order Submitted', ready_to_load: 'Ready to Load',
  loaded: 'Loaded', shipped: 'Shipped', arrived_alg: 'Arrived ALG',
  cleared_port: 'Cleared from Port', delivered: 'Delivered', cancelled: 'Cancelled',
  // Legacy
  draft: 'Draft', active: 'Active', closed: 'Closed',
};

const ORDER_STATUS_COLORS = {
  new_order: 'bg-blue-50 text-blue-700 border-blue-300',
  order_submitted: 'bg-indigo-50 text-indigo-700 border-indigo-300',
  ready_to_load: 'bg-amber-50 text-amber-700 border-amber-300',
  loaded: 'bg-purple-50 text-purple-700 border-purple-300',
  shipped: 'bg-violet-600 text-white border-violet-700',
  arrived_alg: 'bg-teal-50 text-teal-700 border-teal-300',
  cleared_port: 'bg-cyan-50 text-cyan-700 border-cyan-300',
  delivered: 'bg-green-600 text-white border-green-700',
  cancelled: 'bg-red-50 text-red-700 border-red-300',
  draft: 'bg-slate-100 text-slate-600', active: 'bg-emerald-50 text-emerald-700 border-emerald-300',
  closed: 'bg-gray-100 text-gray-600',
};

const EXTRA_FEATURES_OPTIONS = [
  { id: 'led_ambiance', label: 'LED Ambiance' },
  { id: 'siege_electrique_driver', label: 'Electric Seat (Driver)' },
  { id: 'siege_electrique_passenger', label: 'Electric Seat (Passenger)' },
  { id: 'malle_electrique', label: 'Electric Trunk' },
  { id: 'tinted_glass', label: 'Tinted Glass' },
  { id: 'roue_secours', label: 'Spare Tire' },
];

const COLOR_OPTIONS = ['White', 'Silver', 'Grey', 'Black'];

const statusLabel = (s) => ORDER_STATUS_LABELS[s] || (s || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

const StatusBadge = ({ status }) => (
  <span className={`inline-flex items-center px-2 py-0.5 text-[11px] font-semibold rounded-full border ${ORDER_STATUS_COLORS[status] || 'bg-gray-100 text-gray-600'}`} data-testid={`status-badge-${status}`}>
    {statusLabel(status)}
  </span>
);

const Row = ({ label, value, mono }) => (
  <div className="flex justify-between gap-3">
    <span className="text-muted-foreground text-xs flex-shrink-0">{label}</span>
    <span className={`text-sm font-medium text-right ${mono ? 'font-mono' : ''}`}>{value || '-'}</span>
  </div>
);

const SectionTitle = ({ children }) => (
  <h4 className="text-xs font-semibold uppercase text-muted-foreground mb-2 tracking-wider">{children}</h4>
);
const ENGINE_SIZES = ['1.2', '1.4', '1.5', '1.6', '1.8', '2.0'];

const ExtraFeaturesChecklist = ({ selected = [], onChange }) => (
  <div className="grid grid-cols-2 gap-1.5">
    {EXTRA_FEATURES_OPTIONS.map(opt => {
      const checked = selected.includes(opt.id);
      return (
        <label key={opt.id} className={`flex items-center gap-2 px-2 py-1.5 border text-xs cursor-pointer transition ${checked ? 'bg-primary/5 border-primary/40' : 'border-border hover:bg-slate-50'}`}>
          <input type="checkbox" checked={checked} onChange={() => onChange(checked ? selected.filter(f => f !== opt.id) : [...selected, opt.id])} className="w-3.5 h-3.5 accent-primary" />
          {opt.label}
        </label>
      );
    })}
  </div>
);

// Car form fields component
const OrderTimeline = ({ orderId }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await getOrderTimeline(orderId);
        if (!cancelled) setItems(res.data || []);
      } catch (err) {
        console.error('Timeline load failed', err);
      } finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [orderId]);

  const actionLabel = (action) => ({
    order_created: 'Order created',
    car_added: 'Car added',
    status_updated: 'Status updated',
    auto_status_from_tracking: 'Auto status update',
    payment_added: 'Payment recorded',
    document_uploaded: 'Document uploaded',
  }[action] || action.replace(/_/g, ' '));

  if (loading) return <div className="text-sm text-muted-foreground py-4">Loading timeline...</div>;
  if (items.length === 0) return <div className="text-sm text-muted-foreground py-4">No timeline events yet.</div>;
  return (
    <div className="space-y-2" data-testid="order-timeline-list">
      {items.map((evt, idx) => {
        const ts = evt.timestamp ? new Date(evt.timestamp) : null;
        return (
          <div key={`${evt.timestamp}-${idx}`} className="flex gap-3 pb-3 border-b border-border last:border-0">
            <div className="flex flex-col items-center pt-1">
              <div className="w-2 h-2 rounded-full bg-primary" />
              {idx < items.length - 1 && <div className="flex-1 w-px bg-border mt-1" />}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium">{actionLabel(evt.action)}</p>
              {evt.previous_status && evt.status && (
                <p className="text-xs text-muted-foreground"><code className="text-[10px]">{evt.previous_status}</code> → <code className="text-[10px] font-semibold">{evt.status}</code></p>
              )}
              {evt.status && !evt.previous_status && (
                <p className="text-xs text-muted-foreground">Status: <code className="text-[10px]">{evt.status}</code></p>
              )}
              {evt.details && <p className="text-xs text-foreground/80">{evt.details}</p>}
              {evt.notes && <p className="text-[11px] text-muted-foreground italic">{evt.notes}</p>}
              <p className="text-[11px] text-muted-foreground mt-1">
                {ts && ts.toLocaleString(undefined, { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                {evt.changed_by_name && <span> · by <span className="font-medium">{evt.changed_by_name}</span></span>}
              </p>
            </div>
          </div>
        );
      })}
    </div>
  );
};


const CarFormFields = ({ data, onChange, brands = [], models = [], ports = [], canSeeInternal = false }) => {
  const set = (field, value) => onChange({ ...data, [field]: value });
  const currentYear = new Date().getFullYear();
  const isNew = (data.condition || 'new') === 'new';
  // Find selected brand id from brand name (for filtering models)
  const selectedBrand = brands.find(b => b.name === data.brand);
  const availableModels = selectedBrand
    ? models.filter(m => m.brand_id === selectedBrand.id)
    : models;
  return (
    <div className="space-y-3">
      {/* Identity */}
      <div className="grid grid-cols-3 gap-2">
        <div>
          <Label className="text-xs">Brand *</Label>
          {brands.length > 0 ? (
            <Select
              value={data.brand || ''}
              onValueChange={(v) => onChange({ ...data, brand: v, model: '' })}
            >
              <SelectTrigger className="h-8" data-testid="car-field-brand"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>
                {brands.map(b => <SelectItem key={b.id} value={b.name}>{b.name}</SelectItem>)}
              </SelectContent>
            </Select>
          ) : (
            <Input value={data.brand || ''} onChange={(e) => set('brand', e.target.value)} className="h-8" data-testid="car-field-brand" />
          )}
        </div>
        <div>
          <Label className="text-xs">Model *</Label>
          {availableModels.length > 0 ? (
            <Select value={data.model || ''} onValueChange={(v) => set('model', v)} disabled={!data.brand}>
              <SelectTrigger className="h-8" data-testid="car-field-model"><SelectValue placeholder={data.brand ? 'Select' : 'Pick brand first'} /></SelectTrigger>
              <SelectContent>
                {availableModels.map(m => <SelectItem key={m.id} value={m.name}>{m.name}</SelectItem>)}
              </SelectContent>
            </Select>
          ) : (
            <Input value={data.model || ''} onChange={(e) => set('model', e.target.value)} className="h-8" data-testid="car-field-model" placeholder={data.brand ? 'No models — type one' : 'Pick brand first'} />
          )}
        </div>
        <div>
          <Label className="text-xs">Condition</Label>
          <Select value={data.condition || 'new'} onValueChange={(v) => set('condition', v)}>
            <SelectTrigger className="h-8" data-testid="car-field-condition"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="new">New</SelectItem>
              <SelectItem value="used">Used</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="grid grid-cols-3 gap-2">
        <div><Label className="text-xs">VIN</Label><Input value={data.vin || ''} onChange={(e) => set('vin', e.target.value)} className="h-8 font-mono" data-testid="car-field-vin" /></div>
        <div>
          <Label className="text-xs">Color</Label>
          <Select value={data.color || ''} onValueChange={(v) => set('color', v)}>
            <SelectTrigger className="h-8"><SelectValue placeholder="Select" /></SelectTrigger>
            <SelectContent>
              {COLOR_OPTIONS.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        {!isNew && (
          <div><Label className="text-xs">Year</Label><Input type="number" value={data.year || ''} onChange={(e) => set('year', e.target.value)} className="h-8" /></div>
        )}
        {isNew && <div className="pt-5 text-xs text-muted-foreground">Year: {currentYear} (auto)</div>}
      </div>
      {/* Used car fields */}
      {!isNew && (
        <div className="grid grid-cols-2 gap-2 pl-2 border-l-2 border-amber-300">
          <div><Label className="text-xs">Mileage (KM)</Label><Input type="number" value={data.mileage_km || ''} onChange={(e) => set('mileage_km', e.target.value)} className="h-8" data-testid="car-field-km" /></div>
          <div className="flex items-center gap-2 pt-5">
            <Switch checked={data.s_report || false} onCheckedChange={(v) => set('s_report', v)} data-testid="car-field-sreport" />
            <Label className="text-xs">S-Report (Accident)</Label>
          </div>
        </div>
      )}
      {/* Pricing */}
      <div>
        <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-1.5">Pricing (DZD)</p>
        <div className="grid grid-cols-3 gap-2">
          <div><Label className="text-xs">Selling Price</Label><MoneyInput value={data.selling_price} onChange={(v) => set('selling_price', v)} className="h-8" data-testid="car-field-selling-price" /></div>
          <div><Label className="text-xs">CIF Shipping</Label><MoneyInput value={data.cif_price} onChange={(v) => set('cif_price', v)} className="h-8" /></div>
          <div><Label className="text-xs">Shipping Price</Label><MoneyInput value={data.shipping_price} onChange={(v) => set('shipping_price', v)} className="h-8" /></div>
        </div>
      </div>
      {/* Specs */}
      <div>
        <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-1.5">Specifications</p>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <Label className="text-xs">Fuel Type</Label>
            <Select value={data.fuel_type || ''} onValueChange={(v) => set('fuel_type', v)}>
              <SelectTrigger className="h-8"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="gasoline">Gasoline</SelectItem>
                <SelectItem value="hybrid">Hybrid</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Transmission</Label>
            <Select value={data.transmission || ''} onValueChange={(v) => set('transmission', v)}>
              <SelectTrigger className="h-8"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="auto_cvt">Auto CVT</SelectItem>
                <SelectItem value="auto_dct">Auto DCT</SelectItem>
                <SelectItem value="manual">Manual</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2 mt-1">
          <div>
            <Label className="text-xs">Version</Label>
            <Select value={data.version_type || ''} onValueChange={(v) => set('version_type', v)}>
              <SelectTrigger className="h-8"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="international">International</SelectItem>
                <SelectItem value="chinese">Chinese</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Engine</Label>
            <Select value={data.engine_variant || ''} onValueChange={(v) => set('engine_variant', v)}>
              <SelectTrigger className="h-8"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="normal">Normal</SelectItem>
                <SelectItem value="turbo">Turbo</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2 mt-1">
          <div>
            <Label className="text-xs">Engine Size</Label>
            <Select value={data.engine_size || ''} onValueChange={(v) => set('engine_size', v)}>
              <SelectTrigger className="h-8"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>
                {ENGINE_SIZES.map(s => <SelectItem key={s} value={s}>{s}L</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div><Label className="text-xs">HP</Label><Input type="number" value={data.horsepower || ''} onChange={(e) => set('horsepower', e.target.value)} className="h-8" /></div>
        </div>
      </div>
      {/* Extras (features + accessories) — itemized with price and "requested by client" flag */}
      <div>
        <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-1.5">Extras / Accessories</p>
        <ExtrasEditor
          items={data.extras_items || []}
          onChange={(v) => set('extras_items', v)}
        />
      </div>

      {/* Spare wheel — shown only for GS3 model */}
      {(/gs3/i.test(data.model || '')) && (
        <div className="border-l-4 border-amber-400 bg-amber-50 p-3" data-testid="spare-wheel-section">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-bold">Roue de secours (GS3)</p>
            <label className="flex items-center gap-2 text-xs">
              <input
                type="checkbox"
                checked={!!data.spare_wheel_added}
                onChange={(e) => set('spare_wheel_added', e.target.checked)}
                data-testid="spare-wheel-toggle"
              />
              Add spare wheel
            </label>
          </div>
          {data.spare_wheel_added && (
            <div>
              <Label className="text-xs">Spare wheel price (DZD) *</Label>
              <MoneyInput
                value={data.spare_wheel_price}
                onChange={(v) => set('spare_wheel_price', v)}
                placeholder="e.g. 35 000"
                className="h-8"
                data-testid="spare-wheel-price"
              />
              <p className="text-[10px] text-muted-foreground mt-1">Will be added to total. If client refuses, untick and the spare wheel can be resold separately.</p>
            </div>
          )}
        </div>
      )}

      {/* Custom Fees (Transiteur, Dégroupage, Assurance, Livraison...) */}
      <div>
        <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-1.5">Custom Fees (Dédouanement / Logistique)</p>
        <CustomFeesEditor
          items={data.custom_fees || []}
          onChange={(v) => onChange({ ...data, custom_fees: v })}
          canSeeInternal={canSeeInternal}
        />
      </div>

      {/* Port (Dédouanement) */}
      <div>
        <Label className="text-xs">Port (Dédouanement)</Label>
        {ports && ports.length > 0 ? (
          <Select value={data.port_id || 'none'} onValueChange={(v) => onChange({ ...data, port_id: v === 'none' ? null : v })}>
            <SelectTrigger className="h-8" data-testid="car-field-port"><SelectValue placeholder="Select port" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">— None —</SelectItem>
              {ports.map(p => (
                <SelectItem key={p.id} value={p.id}>{p.name}{p.city ? ` (${p.city})` : ''}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : (
          <p className="text-[11px] text-muted-foreground italic mt-1">No ports defined yet. Go to Dédouanement to add ports.</p>
        )}
      </div>
    </div>
  );
};

// Itemized extras list — each row has name, price (DZD), and a "requested by client" toggle.
// Only items with requested_by_client === true count toward the order total.
const ExtrasEditor = ({ items = [], onChange }) => {
  const update = (idx, patch) => {
    const next = items.map((it, i) => (i === idx ? { ...it, ...patch } : it));
    onChange(next);
  };
  const add = () => onChange([...(items || []), { name: '', price: 0, requested_by_client: false }]);
  const remove = (idx) => onChange(items.filter((_, i) => i !== idx));
  const requestedTotal = items
    .filter(it => it.requested_by_client)
    .reduce((sum, it) => sum + (parseFloat(it.price) || 0), 0);
  return (
    <div className="space-y-1.5" data-testid="extras-editor">
      {items.length === 0 && <p className="text-[11px] text-muted-foreground italic">No extras. Click "+ Add" to add an accessory or feature.</p>}
      {items.map((it, idx) => (
        <div key={`extra-row-${idx}`} className="grid grid-cols-[1fr_110px_auto_auto] gap-1.5 items-center">
          <Input
            value={it.name || ''}
            onChange={(e) => update(idx, { name: e.target.value })}
            placeholder="e.g. Floor mats, Camera 360°"
            className="h-8 text-sm"
            data-testid={`extra-name-${idx}`}
          />
          <MoneyInput
            value={it.price}
            onChange={(v) => update(idx, { price: v })}
            placeholder="Price"
            className="h-8 text-sm"
            data-testid={`extra-price-${idx}`}
          />
          <label className="flex items-center gap-1 text-[11px] whitespace-nowrap" title="If checked, price is added to order total">
            <input
              type="checkbox"
              checked={!!it.requested_by_client}
              onChange={(e) => update(idx, { requested_by_client: e.target.checked })}
              data-testid={`extra-requested-${idx}`}
            />
            Requested
          </label>
          <button type="button" onClick={() => remove(idx)} className="text-destructive text-xs px-1 hover:underline" data-testid={`extra-remove-${idx}`}>×</button>
        </div>
      ))}
      <div className="flex items-center justify-between pt-1">
        <button type="button" onClick={add} className="text-xs text-primary hover:underline" data-testid="extra-add-btn">+ Add extra</button>
        {requestedTotal > 0 && (
          <span className="text-[11px] text-muted-foreground">Requested total: <span className="font-semibold text-foreground">{requestedTotal.toLocaleString()} DZD</span></span>
        )}
      </div>
    </div>
  );
};

// End client fields component
const EndClientFields = ({ data, onChange, savedEndClients = [], isIndividual = false }) => {
  const set = (field, value) => onChange({ ...data, [field]: value });
  return (
    <div className="border-t pt-2 mt-3">
      <div className="flex items-center gap-2 mb-1">
        <span className="text-xs font-semibold">End Client</span>
        {savedEndClients.length > 0 && !isIndividual && (
          <div className="flex gap-1 flex-wrap">
            {savedEndClients.map(ben => (
              <button key={ben.id} type="button" onClick={() => onChange({
                ...data, end_client_name: ben.name,
                end_client_nin: ben.id_number || '', end_client_phone: ben.phone || ''
              })} className="text-[10px] px-2 py-0.5 bg-white border hover:bg-blue-50 rounded">{ben.name}</button>
            ))}
          </div>
        )}
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div><Label className="text-xs">Name *</Label><Input value={data.end_client_name || ''} onChange={(e) => set('end_client_name', e.target.value)} className="h-8" data-testid="end-client-name" /></div>
        <div><Label className="text-xs">NIN # *</Label><Input value={data.end_client_nin || ''} onChange={(e) => set('end_client_nin', e.target.value)} className="h-8 font-mono" data-testid="end-client-nin" /></div>
      </div>
      <div className="grid grid-cols-2 gap-2 mt-1">
        <div><Label className="text-xs">Passport #</Label><Input value={data.end_client_passport || ''} onChange={(e) => set('end_client_passport', e.target.value)} className="h-8 font-mono" /></div>
        <div><Label className="text-xs">ID Card #</Label><Input value={data.end_client_id_card || ''} onChange={(e) => set('end_client_id_card', e.target.value)} className="h-8 font-mono" /></div>
      </div>
      <div className="grid grid-cols-2 gap-2 mt-1">
        <div><Label className="text-xs">Phone</Label><Input value={data.end_client_phone || ''} onChange={(e) => set('end_client_phone', e.target.value)} className="h-8" /></div>
        <div><Label className="text-xs">Email</Label><Input type="email" value={data.end_client_email || ''} onChange={(e) => set('end_client_email', e.target.value)} className="h-8" /></div>
      </div>
    </div>
  );
};

// ========== MAIN COMPONENT ==========
const OrdersPage = () => {
  const { t } = useTranslation();
  const { user: currentUser } = useAuth();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [total, setTotal] = useState(0);
  const [clients, setClients] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [shippingCompanies, setShippingCompanies] = useState([]);
  const [stockVehicles, setStockVehicles] = useState([]);
  const [brandsList, setBrandsList] = useState([]);
  const [modelsList, setModelsList] = useState([]);
  const [portsList, setPortsList] = useState([]);
  const [savedEndClients, setSavedEndClients] = useState([]);
  const [agentsList, setAgentsList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState(searchParams.get('status') || 'all');
  const [statusCounts, setStatusCounts] = useState({ total: 0, by_status: {} });
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const [orderDrawerOpen, setOrderDrawerOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [orderLoading, setOrderLoading] = useState(false);

  const [carDrawerOpen, setCarDrawerOpen] = useState(false);
  const [editCarMode, setEditCarMode] = useState(false);
  const [editCarForm, setEditCarForm] = useState(null);
  const [savingCar, setSavingCar] = useState(false);
  const [selectedCar, setSelectedCar] = useState(null);
  const [editCarShipping, setEditCarShipping] = useState({});

  const [createOrderOpen, setCreateOrderOpen] = useState(false);
  const [creatingOrder, setCreatingOrder] = useState(false);
  const creatingOrderRef = useRef(false);
  const [editOrderOpen, setEditOrderOpen] = useState(false);
  const [selectedOrderIds, setSelectedOrderIds] = useState(new Set());
  const [bulkActionStatus, setBulkActionStatus] = useState('');
  const [editOrderForm, setEditOrderForm] = useState({
    client_id: '', supplier_id: '', assigned_agent_id: '', notes: '', deposit_amount: '', status: '',
    client_surname: '', client_given_name: '', client_phone: '', client_email: '',
    client_address: '', client_nin: '', client_passport: '', client_id_card: '', client_company: '',
  });
  const [importOpen, setImportOpen] = useState(false);
  const [importPreview, setImportPreview] = useState(null);

  // Duplicate-order finder
  const [duplicatesOpen, setDuplicatesOpen] = useState(false);
  const [duplicates, setDuplicates] = useState(null);
  const [duplicatesLoading, setDuplicatesLoading] = useState(false);
  const [deletingMongoId, setDeletingMongoId] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [importMode, setImportMode] = useState('template'); // 'template' | 'ai'
  const [aiJobId, setAiJobId] = useState(null);
  const [aiProgress, setAiProgress] = useState(null); // { status, batches_done, total_batches }
  const aiPollRef = useRef(null);
  const [newOrderSummary, setNewOrderSummary] = useState({ total_cars: 0, groups: [], by_status: {} });
  const [summaryStatus, setSummaryStatus] = useState('new_order');
  const [addCarOpen, setAddCarOpen] = useState(false);
  const [poDialogOpen, setPODialogOpen] = useState(false);
  const [paymentWarning, setPaymentWarning] = useState({ open: false, orderNumber: '', outstanding: 0, totalSelling: 0, totalPaid: 0, pendingStatus: '' });
  const [poData, setPOData] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [paymentForm, setPaymentForm] = useState({ amount: '', payment_method: 'especes', date: '', notes: '' });
  const [carPayments, setCarPayments] = useState([]);
  const [sendEmailOpen, setSendEmailOpen] = useState(false);
  const [emailForm, setEmailForm] = useState({ to_emails: '', extra_email: '' });
  const [sendingEmail, setSendingEmail] = useState(false);
  const [orderTrackings, setOrderTrackings] = useState([]);
  const [scacCodes, setScacCodes] = useState([]);
  const [trackingForm, setTrackingForm] = useState({ container_number: '', scac: '' });
  const [trackingLoading, setTrackingLoading] = useState(false);
  const clientSigRef = useRef(null);
  const agentSigRef = useRef(null);

  const [orderForm, setOrderForm] = useState({ client_id: '', supplier_id: '', assigned_agent_id: '', notes: '', deposit_amount: '' });

  const carLineIdRef = useRef(0);

  const emptyCarLine = () => ({
    _lineId: ++carLineIdRef.current,
    brand: '', model: '', year: '', vin: '', color: '', condition: 'new',
    mileage_km: '', s_report: false,
    selling_price: '', cif_price: '', shipping_price: '', purchase_price: '', deposit_amount: '',
    fuel_type: '', version_type: '', transmission: '', engine_variant: '', engine_size: '', horsepower: '',
    extra_features: [], accessories: '',
    end_client_name: '', end_client_nin: '', end_client_passport: '', end_client_id_card: '',
    end_client_phone: '', end_client_email: '',
    shipping_company: '', container_number: '', bl_number: '', eta: '', notes: '',
    stock_vehicle_id: ''
  });

  const [carForm, setCarForm] = useState(emptyCarLine());
  const [carLines, setCarLines] = useState([]);

  const loadOrders = useCallback(async () => {
    try {
      const params = { limit: 5000 };
      if (search) params.search = search;
      if (filterStatus !== 'all') params.status = filterStatus;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const res = await getOrders(params);
      const list = res.data.orders || [];
      setOrders(list);
      setTotal(res.data.total || 0);
      // Drop any stale selection that no longer exists in the current view
      // — prevents bulk actions from silently affecting orders we can't see.
      setSelectedOrderIds((prev) => {
        if (!prev.size) return prev;
        const visible = new Set(list.map((o) => o.id));
        const next = new Set();
        prev.forEach((id) => { if (visible.has(id)) next.add(id); });
        return next.size === prev.size ? prev : next;
      });
      // Load summary for selected status
      try {
        const summary = await getNewOrdersSummary(summaryStatus);
        setNewOrderSummary(summary.data || { total_cars: 0, groups: [], by_status: {} });
      } catch { /* non-blocking */ }
      // Status pill cards — use the SAME search/date filters but ignore the
      // status filter so the cards always show ALL buckets to the user.
      try {
        const countParams = {};
        if (search) countParams.search = search;
        if (dateFrom) countParams.date_from = dateFrom;
        if (dateTo) countParams.date_to = dateTo;
        const cnt = await getOrderStatusCounts(countParams);
        setStatusCounts(cnt.data || { total: 0, by_status: {} });
      } catch { /* non-blocking */ }
    } catch { toast.error('Failed to load orders'); }
    finally { setLoading(false); }
  }, [search, filterStatus, dateFrom, dateTo, summaryStatus]);

  const loadFormData = useCallback(async () => {
    try {
      const [c, s, sh, sv, br, md, pt] = await Promise.all([
        getClients({ limit: 10000 }), getSuppliers({}), getShippingCompanies({}), getStockVehicles({}), getBrands(), getModels({}), getPorts().catch(() => ({ data: [] }))
      ]);
      setClients(c.data.clients || []);
      setSuppliers(s.data || []);
      setShippingCompanies(sh.data || []);
      setStockVehicles(sv.data || []);
      setBrandsList(br.data || []);
      setModelsList(md.data || []);
      setPortsList(pt.data || []);
      // Load agents — admins and logistics_managers can both assign agents
      if (currentUser?.role === 'admin' || currentUser?.role === 'logistics_manager' || currentUser?.role === 'master_admin') {
        try {
          const [agRes, scRes] = await Promise.all([getEmployees(), getScacCodes()]);
          setAgentsList(agRes.data || []);
          setScacCodes(scRes.data || []);
        } catch { /* non-admin */ }
      } else {
        try { const scRes = await getScacCodes(); setScacCodes(scRes.data || []); } catch {}
      }
    } catch { /* silent */ }
  }, [currentUser?.role]);

  useEffect(() => { loadOrders(); }, [loadOrders]);
  useEffect(() => { loadFormData(); }, [loadFormData]);

  const totalCars = orders.reduce((sum, o) => sum + (o.cars_count || 0), 0);

  const openOrder = async (order) => {
    setOrderDrawerOpen(true);
    setOrderLoading(true);
    setSelectedOrder(order);
    setOrderTrackings([]);
    try {
      const [res, trackRes] = await Promise.all([getOrder(order.id), getOrderTracking(order.id)]);
      setSelectedOrder(res.data);
      setOrderTrackings(trackRes.data || []);
    } catch { toast.error('Failed to load order'); }
    finally { setOrderLoading(false); }
  };

  const openCar = async (car) => {
    setCarDrawerOpen(true);
    setSelectedCar(car);
    setCarPayments([]);
    setEditCarMode(false);
    try {
      const res = await getCar(car.order_id, car.id);
      setSelectedCar(res.data);
      setEditCarShipping({
        shipping_company: res.data.shipping_company || '',
        container_number: res.data.container_number || '',
        vessel_number: res.data.vessel_number || '',
        bl_number: res.data.bl_number || '',
        eta: res.data.eta || '',
      });
      // Load payments for this car
      const payRes = await getOrderPayments(car.order_id);
      setCarPayments((payRes.data || []).filter(p => p.car_id === car.id));
    } catch { toast.error('Failed to load car'); }
  };

  const startEditCar = () => {
    if (!selectedCar) return;
    setEditCarForm({ ...selectedCar });
    setEditCarMode(true);
  };

  const saveCarEdit = async () => {
    if (!editCarForm) return;
    setSavingCar(true);
    try {
      const payload = parseCarPayload(editCarForm);
      await updateCar(selectedCar.order_id, selectedCar.id, payload);
      toast.success('Car updated');
      const res = await getCar(selectedCar.order_id, selectedCar.id);
      setSelectedCar(res.data);
      setEditCarMode(false);
      loadOrders();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update car');
    } finally { setSavingCar(false); }
  };

  // Statuses that imply the order is heading out / has shipped — these are
  // the ones we want to warn on when the client still has an outstanding
  // balance (so the agent remembers to chase payment).
  const SHIP_TRIGGER_STATUSES = ['ready_to_load', 'shipped'];

  const applyOrderStatusChange = async (newStatus) => {
    if (!selectedOrder) return;
    try {
      await updateOrderStatus(selectedOrder.id, newStatus);
      toast.success(`Status: ${statusLabel(newStatus)}`);
      const res = await getOrder(selectedOrder.id);
      setSelectedOrder(res.data);
      loadOrders();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to update status'); }
  };

  const handleOrderStatusChange = async (newStatus) => {
    if (!selectedOrder) return;
    // If the order is moving into a shipping stage, check the balance and
    // warn (but don't block) the agent so they remember to follow up on the
    // outstanding amount with the client.
    if (SHIP_TRIGGER_STATUSES.includes(newStatus)) {
      try {
        const balRes = await getOrderBalance(selectedOrder.id);
        const bal = balRes.data || {};
        if (!bal.is_fully_paid && bal.outstanding > 0) {
          setPaymentWarning({
            open: true,
            orderNumber: selectedOrder.order_number,
            outstanding: bal.outstanding,
            totalSelling: bal.total_selling_price,
            totalPaid: bal.total_paid,
            pendingStatus: newStatus,
          });
          return;
        }
      } catch { /* balance lookup is best-effort — never block status change on it */ }
    }
    await applyOrderStatusChange(newStatus);
  };

  const handleDeleteOrder = async () => {
    if (!selectedOrder) return;
    if (!window.confirm(`Delete order ${selectedOrder.order_number}? This will remove all cars and payments.`)) return;
    try {
      await deleteOrder(selectedOrder.id);
      toast.success('Order deleted');
      setOrderDrawerOpen(false);
      setSelectedOrder(null);
      loadOrders();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to delete order'); }
  };

  // Find duplicate orders (same order_number OR same client+brand+model+vin+day)
  const openDuplicatesDialog = async () => {
    setDuplicatesOpen(true);
    setDuplicatesLoading(true);
    setDuplicates(null);
    try {
      const res = await findDuplicateOrders();
      setDuplicates(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to scan');
    } finally {
      setDuplicatesLoading(false);
    }
  };

  const handleDeleteDuplicateTwin = async (member, group) => {
    // Confirm by showing what's about to be removed
    const ok = window.confirm(
      `Delete this duplicate order?\n\n` +
      `Order #: ${member.order_number}\n` +
      `Client: ${member.client_name || '(no client)'}\n` +
      `Cars: ${member.car_count}\n` +
      `Created: ${new Date(member.created_at).toLocaleString()}\n\n` +
      `Other ${group.members.length - 1} member(s) of this duplicate group will be PRESERVED.\n` +
      `This action cannot be undone.`
    );
    if (!ok) return;
    setDeletingMongoId(member.mongo_id);
    try {
      const res = await deleteOrderByMongoId(member.mongo_id);
      toast.success(`Removed duplicate · cars cleaned: ${res.data.cars_deleted}`);
      // Refresh the dialog list + main orders table
      await openDuplicatesDialog();
      loadOrders();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delete failed');
    } finally {
      setDeletingMongoId(null);
    }
  };

  const handleCarShippingUpdate = async () => {
    if (!selectedCar) return;
    try {
      await updateCar(selectedCar.order_id, selectedCar.id, editCarShipping);
      toast.success('Shipping info updated');
      const res = await getCar(selectedCar.order_id, selectedCar.id);
      setSelectedCar(res.data);
    } catch (err) { toast.error(err.response?.data?.detail || 'Update failed'); }
  };

  const handleCarFileUpload = async (e, category) => {
    const file = e.target.files?.[0];
    if (!file || !selectedCar) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('order_id', selectedCar.order_id);
      fd.append('car_id', selectedCar.id);
      fd.append('category', category);
      await uploadFile(fd);
      toast.success('Document uploaded');
      const res = await getCar(selectedCar.order_id, selectedCar.id);
      setSelectedCar(res.data);
    } catch { toast.error('Upload failed'); }
    finally { setUploading(false); e.target.value = ''; }
  };

  const handleViewPO = async (orderId) => {
    try {
      const res = await getOrderPO(orderId);
      setPOData(res.data);
      setPODialogOpen(true);
    } catch { toast.error('Failed to load PO'); }
  };

  const handleAddPayment = async () => {
    if (!selectedCar || !paymentForm.amount) { toast.error('Enter payment amount'); return; }
    try {
      await addPayment(selectedCar.order_id, {
        car_id: selectedCar.id,
        amount: parseFloat(paymentForm.amount),
        payment_method: paymentForm.payment_method,
        date: paymentForm.date || undefined,
        notes: paymentForm.notes || undefined,
      });
      toast.success('Payment recorded');
      setPaymentForm({ amount: '', payment_method: 'especes', date: '', notes: '' });
      const payRes = await getOrderPayments(selectedCar.order_id);
      setCarPayments((payRes.data || []).filter(p => p.car_id === selectedCar.id));
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to record payment'); }
  };

  const handleDeletePayment = async (paymentId) => {
    if (!window.confirm('Delete this payment?')) return;
    try {
      await deletePayment(paymentId);
      toast.success('Payment deleted');
      if (selectedCar) {
        const payRes = await getOrderPayments(selectedCar.order_id);
        setCarPayments((payRes.data || []).filter(p => p.car_id === selectedCar.id));
      }
    } catch { toast.error('Failed to delete'); }
  };

  const openSendEmail = () => {
    const clientEmail = selectedCar?.end_client_email || '';
    setEmailForm({ to_emails: clientEmail, extra_email: '' });
    setSendEmailOpen(true);
  };

  const handleSendAvis = async () => {
    if (!selectedCar) return;
    const emails = [emailForm.to_emails, emailForm.extra_email].filter(e => e && e.includes('@'));
    if (emails.length === 0) { toast.error('Enter at least one email address'); return; }
    setSendingEmail(true);
    try {
      const clientSig = clientSigRef.current && !clientSigRef.current.isEmpty() ? clientSigRef.current.toDataURL('image/png') : null;
      const agentSig = agentSigRef.current && !agentSigRef.current.isEmpty() ? agentSigRef.current.toDataURL('image/png') : null;
      await sendAvisEmail(selectedCar.order_id, {
        order_id: selectedCar.order_id,
        car_id: selectedCar.id,
        to_emails: emails,
        client_signature: clientSig,
        agent_signature: agentSig,
      });
      toast.success('Avis de Versement sent!');
      setSendEmailOpen(false);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to send email'); }
    finally { setSendingEmail(false); }
  };

  const handleCreateTracking = async () => {
    if (!selectedOrder || !trackingForm.container_number || !trackingForm.scac) {
      toast.error('Enter container number and select shipping line'); return;
    }
    setTrackingLoading(true);
    try {
      await createTrackingRequest(trackingForm.container_number, trackingForm.scac, selectedOrder.id);
      toast.success('Tracking request created');
      setTrackingForm({ container_number: '', scac: '' });
      const res = await getOrderTracking(selectedOrder.id);
      setOrderTrackings(res.data || []);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to create tracking'); }
    finally { setTrackingLoading(false); }
  };

  const handleRefreshTracking = async (trackingId) => {
    try {
      const res = await getTrackingStatus(trackingId);
      // Refresh all
      const allRes = await getOrderTracking(selectedOrder.id);
      setOrderTrackings(allRes.data || []);
      toast.success('Tracking updated');
    } catch { toast.error('Failed to refresh'); }
  };

  // === Create Order ===
  const handleClientSelect = async (clientId) => {
    setOrderForm(prev => ({ ...prev, client_id: clientId }));
    setCarLines([]);
    if (clientId) {
      try {
        const res = await getClientBeneficiaries(clientId);
        setSavedEndClients(res.data || []);
      } catch { setSavedEndClients([]); }
    }
  };

  const addCarLine = () => {
    const line = emptyCarLine();
    // Auto-fill end client for individual clients
    if (isIndividual && selectedClient) {
      line.end_client_name = selectedClient.name || `${selectedClient.surname || ''} ${selectedClient.given_name || ''}`.trim();
      line.end_client_nin = selectedClient.nin_number || '';
      line.end_client_passport = selectedClient.passport_number || '';
      line.end_client_id_card = selectedClient.id_card_number || '';
      line.end_client_phone = selectedClient.phone || '';
    }
    setCarLines(prev => [...prev, line]);
  };
  const updateCarLine = (idx, updates) => setCarLines(prev => { const c = [...prev]; c[idx] = { ...c[idx], ...updates }; return c; });
  const removeCarLine = (idx) => setCarLines(prev => prev.filter((_, i) => i !== idx));

  const addFromStock = (vehicle) => {
    if (isIndividual && carLines.length >= 1) return; // individual can only have 1 car
    const line = emptyCarLine();
    line.stock_vehicle_id = vehicle.id;
    line.brand = vehicle.brand_name || '';
    line.model = vehicle.model_name || '';
    line.year = vehicle.year?.toString() || '';
    line.color = vehicle.color || '';
    line.fuel_type = vehicle.fuel_type?.toLowerCase() || '';
    line.transmission = vehicle.transmission?.toLowerCase() || '';
    line.purchase_price = vehicle.purchase_price?.toString() || '';
    line.selling_price = vehicle.selling_price?.toString() || '';
    line.condition = 'new';
    // Auto-fill end client for individual clients
    if (isIndividual && selectedClient) {
      line.end_client_name = selectedClient.name || `${selectedClient.surname || ''} ${selectedClient.given_name || ''}`.trim();
      line.end_client_nin = selectedClient.nin_number || '';
      line.end_client_passport = selectedClient.passport_number || '';
      line.end_client_id_card = selectedClient.id_card_number || '';
      line.end_client_phone = selectedClient.phone || '';
    }
    setCarLines(prev => [...prev, line]);
  };

  const currentYear = new Date().getFullYear();
  const parseCarPayload = (c) => ({
    ...c,
    year: c.condition === 'new' ? currentYear : (c.year ? parseInt(c.year) : null),
    purchase_price: c.purchase_price ? parseFloat(c.purchase_price) : null,
    selling_price: c.selling_price ? parseFloat(c.selling_price) : null,
    cif_price: c.cif_price ? parseFloat(c.cif_price) : null,
    shipping_price: c.shipping_price ? parseFloat(c.shipping_price) : null,
    deposit_amount: c.deposit_amount ? parseFloat(c.deposit_amount) : null,
    mileage_km: c.mileage_km ? parseInt(c.mileage_km) : null,
    horsepower: c.horsepower ? parseInt(c.horsepower) : null,
    extra_features: c.extra_features?.length ? c.extra_features : null,
    accessories: c.accessories || null,
    extras_items: c.extras_items?.length
      ? c.extras_items
          .filter(x => x.name)
          .map(x => ({ name: x.name, price: parseFloat(x.price) || 0, requested_by_client: !!x.requested_by_client }))
      : null,
    custom_fees: c.custom_fees?.length
      ? c.custom_fees
          .filter(x => x.name)
          .map(x => ({
            name: x.name,
            amount: parseFloat(x.amount) || 0,
            internal_amount: parseFloat(x.internal_amount) || 0,
            is_paid: !!x.is_paid,
          }))
      : null,
    port_id: c.port_id || null,
    spare_wheel_added: c.spare_wheel_added != null ? !!c.spare_wheel_added : null,
    spare_wheel_price: c.spare_wheel_price ? parseFloat(c.spare_wheel_price) : null,
    s_report: c.condition === 'used' ? c.s_report : null,
    stock_vehicle_id: c.stock_vehicle_id || null,
    fuel_type: c.fuel_type || null,
    end_client_passport: c.end_client_passport || null,
    end_client_id_card: c.end_client_id_card || null,
    end_client_email: c.end_client_email || null,
  });

  const openEditOrder = (order) => {
    const c = order.client || {};
    setEditOrderForm({
      client_id: order.client_id || '',
      supplier_id: order.supplier_id || '',
      assigned_agent_id: order.assigned_agent_id || '',
      notes: order.notes || '',
      deposit_amount: order.deposit_amount != null ? String(order.deposit_amount) : '',
      status: order.status || '',
      client_surname: c.surname || (c.name?.split(' ')?.[0] || ''),
      client_given_name: c.given_name || (c.name?.split(' ')?.slice(1).join(' ') || ''),
      client_phone: c.phone || '',
      client_email: c.email || '',
      client_address: c.address || '',
      client_nin: c.nin_number || c.national_id || '',
      client_passport: c.passport_number || '',
      client_id_card: c.id_card_number || '',
      client_company: c.company_name || '',
    });
    setEditOrderOpen(true);
  };

  const handleSaveOrderEdit = async (e) => {
    e.preventDefault();
    try {
      // 1) Update client record (if any client field has a non-empty value)
      const clientId = editOrderForm.client_id;
      if (clientId) {
        const clientPayload = {
          surname: editOrderForm.client_surname || undefined,
          given_name: editOrderForm.client_given_name || undefined,
          phone: editOrderForm.client_phone || undefined,
          email: editOrderForm.client_email || undefined,
          address: editOrderForm.client_address || undefined,
          nin_number: editOrderForm.client_nin || undefined,
          passport_number: editOrderForm.client_passport || undefined,
          id_card_number: editOrderForm.client_id_card || undefined,
          company_name: editOrderForm.client_company || undefined,
        };
        // Only call if at least one field has changed (we send what's set)
        const hasClientUpdate = Object.values(clientPayload).some(v => v !== undefined && v !== '');
        if (hasClientUpdate) {
          try { await updateClient(clientId, clientPayload); } catch (err) { console.error('Client update failed', err); }
        }
      }
      // 2) Update order
      const orderPayload = {
        client_id: editOrderForm.client_id || undefined,
        supplier_id: editOrderForm.supplier_id || null,
        assigned_agent_id: editOrderForm.assigned_agent_id || null,
        notes: editOrderForm.notes || null,
        deposit_amount: editOrderForm.deposit_amount !== '' ? parseFloat(editOrderForm.deposit_amount) : null,
        status: editOrderForm.status || undefined,
      };
      await updateOrder(selectedOrder.id, orderPayload);
      toast.success('Order updated');
      setEditOrderOpen(false);
      const res = await getOrder(selectedOrder.id);
      setSelectedOrder(res.data);
      loadOrders();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update order');
    }
  };


  const handleCreateOrder = async (e) => {
    e.preventDefault();
    // Guard against double-submit (rapid clicks, Enter-key re-fire). The ref
    // is checked synchronously so even back-to-back submits in the same tick
    // can't slip through before React re-renders.
    if (creatingOrderRef.current) return;
    if (!orderForm.client_id) { toast.error('Select a client'); return; }
    if (carLines.length === 0) { toast.error('Add at least one car'); return; }
    for (let i = 0; i < carLines.length; i++) {
      if (!carLines[i].brand || !carLines[i].model) { toast.error(`Car ${i + 1}: Brand and Model required`); return; }
      if (!carLines[i].end_client_name || !carLines[i].end_client_nin) {
        toast.error(`Car ${i + 1}: End client name and NIN required`); return;
      }
    }
    creatingOrderRef.current = true;
    setCreatingOrder(true);
    try {
      const payload = {
        client_id: orderForm.client_id,
        supplier_id: orderForm.supplier_id || null,
        assigned_agent_id: orderForm.assigned_agent_id || null,
        deposit_amount: orderForm.deposit_amount ? parseFloat(orderForm.deposit_amount) : null,
        notes: orderForm.notes || null,
        cars: carLines.map(parseCarPayload)
      };
      await createOrder(payload);
      toast.success('Order created');
      setCreateOrderOpen(false);
      setOrderForm({ client_id: '', supplier_id: '', notes: '', deposit_amount: '' });
      setCarLines([]);
      loadOrders();
      loadFormData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create order');
    } finally {
      creatingOrderRef.current = false;
      setCreatingOrder(false);
    }
  };

  const handleAddCar = async (e) => {
    e.preventDefault();
    if (!selectedOrder) return;
    if (!carForm.brand || !carForm.model) { toast.error('Brand and Model required'); return; }
    if (!carForm.end_client_name || !carForm.end_client_nin) { toast.error('End client name and NIN required'); return; }
    try {
      await addCarToOrder(selectedOrder.id, parseCarPayload(carForm));
      toast.success('Car added');
      setAddCarOpen(false);
      const res = await getOrder(selectedOrder.id);
      setSelectedOrder(res.data);
      loadOrders();
      loadFormData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to add car'); }
  };

  const handleDeleteCar = async (car) => {
    if (!window.confirm('Delete this car from the order?')) return;
    try {
      await deleteCar(car.order_id, car.id);
      toast.success('Car removed');
      const res = await getOrder(selectedOrder.id);
      setSelectedOrder(res.data);
      loadOrders();
    } catch { toast.error('Failed to delete car'); }
  };

  const selectedClient = clients.find(c => c.user_id === orderForm.client_id);
  const isIndividual = selectedClient?.client_type === 'individual';
  const fmtDZD = (v) => v != null ? `${Number(v).toLocaleString()} DZD` : '-';
  const canSeePurchasePrice = currentUser?.can_see_purchase_price === true || currentUser?.role === 'admin';
  const canSeeSellPrice = currentUser?.role === 'admin' || currentUser?.permissions?.see_sell_price;

  return (
    <div data-testid="orders-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">{t('orders.title')}</h1>
          <p className="text-muted-foreground text-sm">Track orders and shipments</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => openDuplicatesDialog()} data-testid="find-duplicates-btn" title="Find duplicate orders">
            🔍 Find duplicates
          </Button>
          <Button variant="outline" onClick={() => { setImportPreview(null); setImportResult(null); setImportOpen(true); }} data-testid="import-orders-btn">Import Excel</Button>
          <Button onClick={() => { setOrderForm({ client_id: '', supplier_id: '', assigned_agent_id: '', notes: '', deposit_amount: '' }); setCarLines([]); setCreateOrderOpen(true); }} data-testid="add-order-btn">+ New Order</Button>
        </div>
      </div>

      {/* Stat Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
        {[
          { label: 'Total Orders', value: total, color: 'text-foreground' },
          { label: 'Total Cars', value: totalCars, color: 'text-blue-600' },
          { label: 'Stock Vehicles', value: stockVehicles.length, color: 'text-violet-600' },
        ].map(s => (
          <div key={s.label} className="p-4 border border-border bg-white" data-testid={`stat-${s.label.toLowerCase().replace(/\s/g, '-')}`}>
            <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-1">{s.label}</p>
            <p className={`text-2xl font-bold ${s.color}`}>{s.value}</p>
          </div>
        ))}
      </div>

      {/* Status pill cards — one per lifecycle stage, click to filter */}
      <StatusPills
        counts={statusCounts.by_status || {}}
        totalAll={statusCounts.total || 0}
        active={filterStatus}
        onSelect={setFilterStatus}
      />

      {/* Filters */}
      <div className="bg-white border border-border p-3 mb-4 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[180px]">
          <Input placeholder="Search order #..." value={search} onChange={(e) => setSearch(e.target.value)} className="h-9" data-testid="order-search" />
        </div>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-[180px] h-9" data-testid="filter-status"><SelectValue placeholder="All" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Orders</SelectItem>
            {ORDER_STATUSES.map(s => <SelectItem key={s} value={s}>{statusLabel(s)}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="flex items-center gap-1.5">
          <Label className="text-xs text-muted-foreground whitespace-nowrap">From</Label>
          <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="h-9 w-[140px]" data-testid="filter-date-from" />
        </div>
        <div className="flex items-center gap-1.5">
          <Label className="text-xs text-muted-foreground whitespace-nowrap">To</Label>
          <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="h-9 w-[140px]" data-testid="filter-date-to" />
        </div>
        {(dateFrom || dateTo || filterStatus !== 'all') && (
          <Button variant="ghost" size="sm" onClick={() => { setDateFrom(''); setDateTo(''); setFilterStatus('all'); }} className="text-xs h-9" data-testid="clear-filters">Clear</Button>
        )}
      </div>

      {/* Orders Table */}
      {loading ? (
        <div className="flex justify-center py-16"><div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>
      ) : orders.length === 0 ? (
        <div className="text-center py-16 bg-white border border-border text-muted-foreground">
          <p className="text-base font-medium">No orders found</p>
          <p className="text-sm mt-1">Create your first order to get started</p>
        </div>
      ) : (
        <div className="bg-white border border-border overflow-x-auto">
          {selectedOrderIds.size > 0 && (
            <div className="px-4 py-2 bg-primary/5 border-b border-primary/20 flex flex-wrap items-center gap-2" data-testid="bulk-action-bar">
              <span className="text-sm font-medium" data-testid="bulk-selected-count">{selectedOrderIds.size} selected</span>
              <div className="flex-1" />
              <Select value={bulkActionStatus} onValueChange={async (newStatus) => {
                setBulkActionStatus(newStatus);
                if (!newStatus) return;
                if (!window.confirm(`Change status to "${statusLabel(newStatus)}" for ${selectedOrderIds.size} orders?`)) {
                  setBulkActionStatus('');
                  return;
                }
                try {
                  const res = await bulkOrdersAction({ order_ids: Array.from(selectedOrderIds), action: 'set_status', status: newStatus });
                  toast.success(`${res.data.modified} orders updated`);
                  setSelectedOrderIds(new Set());
                  setBulkActionStatus('');
                  loadOrders();
                } catch (err) {
                  toast.error(err.response?.data?.detail || 'Bulk update failed');
                  setBulkActionStatus('');
                }
              }}>
                <SelectTrigger className="h-8 w-44" data-testid="bulk-status-select"><SelectValue placeholder="Change status to..." /></SelectTrigger>
                <SelectContent>{ORDER_STATUSES.map(s => <SelectItem key={s} value={s}>{statusLabel(s)}</SelectItem>)}</SelectContent>
              </Select>
              <Button variant="destructive" size="sm" data-testid="bulk-delete-btn" onClick={async () => {
                if (!window.confirm(`Delete ${selectedOrderIds.size} orders? This cannot be undone.`)) return;
                try {
                  const res = await bulkOrdersAction({ order_ids: Array.from(selectedOrderIds), action: 'delete' });
                  toast.success(`${res.data.deleted_orders} orders deleted`);
                  setSelectedOrderIds(new Set());
                  loadOrders();
                } catch (err) { toast.error(err.response?.data?.detail || 'Bulk delete failed'); }
              }}>Delete</Button>
              <Button variant="ghost" size="sm" onClick={() => setSelectedOrderIds(new Set())} data-testid="bulk-clear-btn">Clear</Button>
            </div>
          )}
          <table className="w-full" data-testid="orders-table">
            <thead>
              <tr className="bg-slate-50 border-b border-border">
                <th className="px-2 py-3 w-8">
                  <input
                    type="checkbox"
                    checked={orders.length > 0 && orders.every(o => selectedOrderIds.has(o.id))}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedOrderIds(new Set([...selectedOrderIds, ...orders.map(o => o.id)]));
                      } else {
                        const next = new Set(selectedOrderIds);
                        orders.forEach(o => next.delete(o.id));
                        setSelectedOrderIds(next);
                      }
                    }}
                    data-testid="bulk-select-all"
                    aria-label="Select all"
                  />
                </th>
                <th className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Order #</th>
                <th className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Client</th>
                <th className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Car</th>
                <th className="px-4 py-3 text-center text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Qty</th>
                <th className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Status</th>
                <th className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-muted-foreground hidden md:table-cell">Created</th>
                <th className="px-4 py-3 w-16"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {orders.map((order) => (
                <tr key={order.id} className="hover:bg-slate-50/80 cursor-pointer transition-colors" onClick={(e) => { if (e.target.tagName !== 'INPUT') openOrder(order); }} data-testid={`order-row-${order.id}`}>
                  <td className="px-2 py-3" onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={selectedOrderIds.has(order.id)}
                      onChange={(e) => {
                        const next = new Set(selectedOrderIds);
                        if (e.target.checked) next.add(order.id);
                        else next.delete(order.id);
                        setSelectedOrderIds(next);
                      }}
                      data-testid={`bulk-select-${order.id}`}
                      aria-label={`Select ${order.order_number}`}
                    />
                  </td>
                  <td className="px-4 py-3"><span className="font-mono text-sm font-semibold text-primary">{order.order_number}</span></td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-sm font-medium">{order.client_name || '-'}</p>
                      {order.client_type && (
                        <span
                          className={`text-[10px] font-semibold uppercase px-1.5 py-0.5 rounded ${order.client_type === 'dealer' ? 'bg-violet-100 text-violet-700' : 'bg-blue-100 text-blue-700'}`}
                          data-testid={`client-type-${order.id}`}
                        >
                          {order.client_type === 'dealer' ? 'Dealer' : 'Individual'}
                        </span>
                      )}
                      {order.source === 'dealer_portal' && (
                        <span
                          className="text-[10px] font-semibold uppercase px-1.5 py-0.5 rounded bg-amber-100 text-amber-800"
                          title={order.submitted_by_name ? `Submitted online by ${order.submitted_by_name}` : 'Submitted online by dealer'}
                          data-testid={`source-online-${order.id}`}
                        >
                          Online
                        </span>
                      )}
                    </div>
                    {order.company_name && <p className="text-[11px] text-muted-foreground">{order.company_name}</p>}
                  </td>
                  <td className="px-4 py-3">
                    {order.cars_preview?.[0] ? (
                      <div>
                        <span className="text-sm">{order.cars_preview[0].brand} {order.cars_preview[0].model}</span>
                        {order.cars_preview[0].color && (
                          <span className="text-[11px] text-muted-foreground ml-2">• {order.cars_preview[0].color}</span>
                        )}
                      </div>
                    ) : '-'}
                  </td>
                  <td className="px-4 py-3 text-center"><span className="text-sm font-bold">{order.cars_count || 0}</span></td>
                  <td className="px-4 py-3"><StatusBadge status={order.status} /></td>
                  <td className="px-4 py-3 hidden md:table-cell">
                    {order.created_at ? (
                      <div>
                        <p className="text-xs text-foreground">{new Date(order.created_at).toLocaleString(undefined, { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</p>
                        {order.created_by_name && (
                          <p className="text-[11px] text-muted-foreground">by {order.created_by_name}</p>
                        )}
                      </div>
                    ) : <span className="text-xs text-muted-foreground">-</span>}
                  </td>
                  <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                    <Button variant="outline" size="sm" data-testid={`edit-row-${order.id}`} onClick={async () => {
                      // Load full order (with client info) before opening edit
                      try {
                        const res = await getOrder(order.id);
                        setSelectedOrder(res.data);
                        openEditOrder(res.data);
                      } catch { toast.error('Failed to load order'); }
                    }}>Edit</Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Cars Summary — grouped by Brand/Model/Color per status */}
      <div className="mt-6 bg-white border border-border" data-testid="cars-summary">
        <div className="px-4 py-3 border-b border-border bg-slate-50">
          <div className="flex items-center justify-between mb-2">
            <div>
              <h3 className="text-sm font-bold uppercase tracking-wider">Cars Summary by Status</h3>
              <p className="text-[11px] text-muted-foreground">Quantity per Brand · Model · Color — pick a status</p>
            </div>
            <span className="text-2xl font-bold text-primary" data-testid="summary-total-cars">{newOrderSummary.total_cars}</span>
          </div>
          <div className="flex flex-wrap gap-1">
            {[
              { key: 'new_order', label: 'New' },
              { key: 'order_submitted', label: 'Submitted' },
              { key: 'ready_to_load', label: 'Ready' },
              { key: 'loaded', label: 'Loaded' },
              { key: 'shipped', label: 'Shipped' },
              { key: 'arrived_alg', label: 'Arrived ALG' },
              { key: 'cleared_port', label: 'Cleared' },
              { key: 'delivered', label: 'Delivered' },
              { key: 'all', label: 'All' },
            ].map(tab => (
              <button
                key={tab.key}
                onClick={() => setSummaryStatus(tab.key)}
                className={`px-3 py-1 text-xs font-medium border ${summaryStatus === tab.key ? 'bg-primary text-white border-primary' : 'bg-white border-border hover:bg-slate-100'}`}
                data-testid={`summary-tab-${tab.key}`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>
        {newOrderSummary.total_cars === 0 ? (
          <div className="px-4 py-8 text-center text-sm text-muted-foreground" data-testid="summary-empty">No cars in this status</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-slate-50/50 border-b border-border">
                  <th className="px-4 py-2 text-left text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Brand</th>
                  <th className="px-4 py-2 text-left text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Model</th>
                  <th className="px-4 py-2 text-left text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Color</th>
                  <th className="px-4 py-2 text-right text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Qty</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {newOrderSummary.groups.map((g) => (
                  <tr key={`${g.brand}-${g.model}-${g.color}`} className="hover:bg-slate-50/80" data-testid={`summary-row-${g.brand}-${g.model}-${g.color}`}>
                    <td className="px-4 py-2 text-sm font-medium">{g.brand}</td>
                    <td className="px-4 py-2 text-sm">{g.model}</td>
                    <td className="px-4 py-2 text-sm text-muted-foreground">{g.color}</td>
                    <td className="px-4 py-2 text-right text-sm font-bold text-primary">{g.count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ===== ORDER DETAIL DRAWER ===== */}
      <Sheet open={orderDrawerOpen} onOpenChange={setOrderDrawerOpen}>
        <SheetContent side="right" className="w-full sm:max-w-[620px] p-0 flex flex-col" data-testid="order-drawer">
          {selectedOrder && (
            <>
              <SheetHeader className="p-5 pb-3 border-b bg-slate-50">
                <SheetTitle className="font-mono text-lg">{selectedOrder.order_number}</SheetTitle>
                <SheetDescription className="flex items-center gap-2 mt-1">
                  <StatusBadge status={selectedOrder.status} />
                  <span className="text-xs text-muted-foreground">{selectedOrder.client?.name}</span>
                </SheetDescription>
                <div className="flex flex-wrap items-center gap-2 mt-2">
                  <Button
                    size="sm" variant="outline"
                    onClick={() => navigate(`/orders/${selectedOrder.id}#documents`)}
                    data-testid="open-order-docs-btn"
                  >
                    📎 Documents & client upload link
                  </Button>
                </div>
              </SheetHeader>
              <div className="flex-1 overflow-y-auto">
                <Tabs defaultValue="cars" className="h-full">
                  <TabsList className="w-full justify-start px-5 pt-2 bg-white rounded-none border-b gap-1">
                    <TabsTrigger value="cars">Cars ({selectedOrder.cars?.length || 0})</TabsTrigger>
                    <TabsTrigger value="status">Status</TabsTrigger>
                    <TabsTrigger value="po">PO</TabsTrigger>
                    <TabsTrigger value="tracking" data-testid="order-tab-tracking">Tracking</TabsTrigger>
                    <TabsTrigger value="timeline" data-testid="order-tab-timeline">Timeline</TabsTrigger>
                  </TabsList>

                  <TabsContent value="cars" className="p-4 m-0">
                    <div className="mb-3 flex items-center justify-end">
                      <Button size="sm" variant="outline" onClick={() => openEditOrder(selectedOrder)} data-testid="edit-order-btn">Edit Order Details</Button>
                    </div>
                    {/* Deposit info */}
                    {selectedOrder.deposit_amount != null && (
                      <div className="mb-3 p-2 bg-blue-50 border border-blue-200 text-sm">
                        <Row label="Order Deposit" value={fmtDZD(selectedOrder.deposit_amount)} />
                      </div>
                    )}
                    <div className="flex items-center justify-between mb-3">
                      <SectionTitle>Cars</SectionTitle>
                      {selectedOrder.client?.client_type === 'dealer' && (
                        <Button size="sm" variant="outline" onClick={() => { setCarForm(emptyCarLine()); setAddCarOpen(true); }} data-testid="add-car-btn">+ Add Car</Button>
                      )}
                    </div>
                    {orderLoading ? (
                      <div className="flex justify-center py-6"><div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>
                    ) : !selectedOrder.cars?.length ? (
                      <div className="text-center py-6 border border-dashed text-sm text-muted-foreground">No cars in this order yet</div>
                    ) : (
                      <div className="space-y-2">
                        {selectedOrder.cars.map((car) => (
                          <div key={car.id} className="border p-3 hover:bg-blue-50/40 cursor-pointer" onClick={() => openCar({ ...car, order_id: selectedOrder.id })} data-testid={`car-row-${car.id}`}>
                            <div className="flex justify-between items-start">
                              <div>
                                <p className="text-sm font-semibold">{car.brand} {car.model} {car.year ? `(${car.year})` : ''}</p>
                                <p className="text-xs text-muted-foreground">{car.end_client_name || '-'} | NIN: {car.end_client_nin || '-'}</p>
                              </div>
                              <div className="text-right">
                                {car.selling_price && <p className="text-sm font-bold">{fmtDZD(car.selling_price)}</p>}
                                {car.deposit_amount > 0 && <p className="text-[10px] text-green-600">Deposit: {fmtDZD(car.deposit_amount)}</p>}
                              </div>
                            </div>
                            {(car.bl_number || car.container_number) && (
                              <div className="flex gap-3 mt-1 text-[10px] text-muted-foreground font-mono">
                                {car.bl_number && <span>BL: {car.bl_number}</span>}
                                {car.container_number && <span>CNT: {car.container_number}</span>}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </TabsContent>

                  <TabsContent value="status" className="p-4 m-0">
                    <div className="mb-4">
                      <SectionTitle>Current Status</SectionTitle>
                      <StatusBadge status={selectedOrder.status} />
                    </div>
                    {/* Agent Assignment */}
                    <div className="mb-4">
                      <SectionTitle>Assigned Agent</SectionTitle>
                      {selectedOrder.agent ? (
                        <p className="text-sm font-medium">{selectedOrder.agent.name} <span className="text-muted-foreground text-xs">({selectedOrder.agent.phone || selectedOrder.agent.email})</span></p>
                      ) : <p className="text-xs text-muted-foreground">Not assigned</p>}
                      {agentsList.length > 0 && (
                        <Select value={selectedOrder.assigned_agent_id || ''} onValueChange={async (v) => {
                          try {
                            await updateOrder(selectedOrder.id, { assigned_agent_id: v });
                            toast.success('Agent reassigned');
                            const res = await getOrder(selectedOrder.id);
                            setSelectedOrder(res.data);
                            loadOrders();
                          } catch { toast.error('Failed to reassign'); }
                        }}>
                          <SelectTrigger className="h-8 mt-1" data-testid="reassign-agent"><SelectValue placeholder="Reassign agent..." /></SelectTrigger>
                          <SelectContent>{agentsList.map(a => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}</SelectContent>
                        </Select>
                      )}
                    </div>
                    <SectionTitle>Change Status</SectionTitle>
                    <div className="grid grid-cols-2 gap-1.5 mb-4">
                      {ORDER_STATUSES.map((s) => (
                        <button key={s} onClick={() => handleOrderStatusChange(s)} data-testid={`set-status-${s}`}
                          className={`p-2 text-xs text-left border transition ${selectedOrder.status === s ? 'border-primary bg-primary/10 font-semibold' : 'border-border hover:border-primary/40'}`}>
                          {statusLabel(s)}
                        </button>
                      ))}
                    </div>
                    <div className="pt-3 border-t">
                      <Button variant="destructive" size="sm" onClick={handleDeleteOrder} data-testid="delete-order-btn">Delete Order</Button>
                    </div>
                  </TabsContent>

                  <TabsContent value="po" className="p-4 m-0">
                    <div className="space-y-3">
                      <Button onClick={() => handleViewPO(selectedOrder.id)} className="w-full" data-testid="view-po-btn">View Purchase Order</Button>
                      <a href={getOrderPOPdfUrl(selectedOrder.id)} target="_blank" rel="noopener noreferrer" className="block">
                        <Button variant="outline" className="w-full" data-testid="download-po-btn">Download PO PDF (Avis de Versement)</Button>
                      </a>
                      <a href={getDeliveryNotePdfUrl(selectedOrder.id)} target="_blank" rel="noopener noreferrer" className="block">
                        <Button variant="outline" className="w-full" data-testid="download-delivery-note-btn">Download Bon de Livraison</Button>
                      </a>
                      <Button variant="outline" className="w-full" data-testid="copy-tracking-link" onClick={() => {
                        const url = `${window.location.origin}/track`;
                        navigator.clipboard.writeText(url);
                        toast.success(`Tracking link copied: ${url}`);
                      }}>Copy Client Tracking Link</Button>
                    </div>
                  </TabsContent>

                  <TabsContent value="tracking" className="p-4 m-0">
                    {/* Create Tracking */}
                    <div className="border p-3 mb-4 space-y-2">
                      <SectionTitle>Track Container</SectionTitle>
                      {(() => {
                        // Collect unique containers and BLs from cars
                        const containers = [...new Set((selectedOrder?.cars || []).map(c => c.container_number).filter(Boolean))];
                        return containers.length > 0 ? (
                          <div>
                            <Label className="text-xs">Select from order cars</Label>
                            <Select value={trackingForm.container_number} onValueChange={(v) => setTrackingForm(prev => ({ ...prev, container_number: v }))}>
                              <SelectTrigger className="h-8 font-mono" data-testid="tracking-container-select"><SelectValue placeholder="Select container" /></SelectTrigger>
                              <SelectContent>
                                {containers.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                              </SelectContent>
                            </Select>
                          </div>
                        ) : (
                          <div>
                            <Label className="text-xs">Container #</Label>
                            <Input value={trackingForm.container_number} onChange={(e) => setTrackingForm(prev => ({ ...prev, container_number: e.target.value }))} className="h-8 font-mono" placeholder="MSCU1234567" data-testid="tracking-container" />
                          </div>
                        );
                      })()}
                      <div>
                        <Label className="text-xs">Shipping Line</Label>
                        <Select value={trackingForm.scac} onValueChange={(v) => setTrackingForm(prev => ({ ...prev, scac: v }))}>
                          <SelectTrigger className="h-8" data-testid="tracking-scac"><SelectValue placeholder="Select" /></SelectTrigger>
                          <SelectContent>
                            {scacCodes.map(s => <SelectItem key={s.scac} value={s.scac}>{s.name} ({s.scac})</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <Button onClick={handleCreateTracking} disabled={trackingLoading} size="sm" className="w-full" data-testid="create-tracking-btn">
                        {trackingLoading ? 'Creating...' : 'Start Tracking'}
                      </Button>
                    </div>

                    {/* Tracking Results */}
                    {orderTrackings.length === 0 ? (
                      <div className="text-center py-6 border border-dashed text-sm text-muted-foreground">No tracking requests yet</div>
                    ) : (
                      <div className="space-y-3">
                        {orderTrackings.map(t => (
                          <div key={t.id} className="border p-3" data-testid={`tracking-${t.id}`}>
                            <div className="flex items-center justify-between mb-2">
                              <span className="font-mono text-sm font-bold">{t.container_number}</span>
                              <div className="flex items-center gap-2">
                                <Badge variant={t.status === 'succeeded' ? 'default' : 'secondary'} className="text-[10px]">
                                  {t.status || 'pending'}
                                </Badge>
                                <button onClick={() => handleRefreshTracking(t.id)} className="text-xs text-primary hover:underline">Refresh</button>
                              </div>
                            </div>
                            {t.tracking_data && (
                              <div className="space-y-1 text-sm">
                                {t.tracking_data.shipping_line && <Row label="Line" value={t.tracking_data.shipping_line} />}
                                {t.tracking_data.vessel_name && <Row label="Vessel" value={`${t.tracking_data.vessel_name} ${t.tracking_data.voyage_number || ''}`} />}
                                {t.tracking_data.port_of_lading && <Row label="From" value={t.tracking_data.port_of_lading} />}
                                {t.tracking_data.port_of_discharge && <Row label="To" value={t.tracking_data.port_of_discharge} />}
                                {t.tracking_data.pol_atd && <Row label="Departed" value={t.tracking_data.pol_atd?.slice(0, 10)} />}
                                {t.tracking_data.pod_eta && <Row label="ETA" value={t.tracking_data.pod_eta?.slice(0, 10)} />}
                                {t.tracking_data.pod_ata && <Row label="Arrived" value={t.tracking_data.pod_ata?.slice(0, 10)} />}
                                {t.tracking_data.status && <Row label="Status" value={t.tracking_data.status} />}
                              </div>
                            )}
                            {t.containers?.length > 0 && t.containers.map((c, i) => (
                              <div key={i} className="mt-2 pt-2 border-t text-xs space-y-0.5">
                                <Row label="Container" value={c.number} mono />
                                {c.status && <Row label="Status" value={c.status} />}
                                {c.location && <Row label="Location" value={c.location} />}
                                {c.pod_eta && <Row label="ETA" value={c.pod_eta?.slice(0, 10)} />}
                              </div>
                            ))}
                            {!t.tracking_data && t.status === 'pending' && (
                              <p className="text-xs text-muted-foreground mt-1">Processing... Click Refresh in a moment</p>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </TabsContent>

                  <TabsContent value="timeline" className="p-4 m-0">
                    <OrderTimeline orderId={selectedOrder.id} />
                  </TabsContent>
                </Tabs>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>

      {/* ===== CAR DETAIL DRAWER ===== */}
      <Sheet open={carDrawerOpen} onOpenChange={setCarDrawerOpen}>
        <SheetContent side="right" className="w-full sm:max-w-[540px] p-0 flex flex-col" data-testid="car-drawer">
          {selectedCar && (
            <>
              <SheetHeader className="p-5 pb-3 border-b bg-slate-50">
                <SheetTitle className="text-base">{selectedCar.brand} {selectedCar.model} {selectedCar.year ? `(${selectedCar.year})` : ''}</SheetTitle>
                <SheetDescription className="flex items-center gap-2 mt-1">
                  {selectedCar.vin && <span className="text-[10px] font-mono bg-slate-100 px-1.5 py-0.5">VIN: {selectedCar.vin}</span>}
                  {selectedCar.bl_number && <span className="text-xs font-mono">BL: {selectedCar.bl_number}</span>}
                </SheetDescription>
              </SheetHeader>
              <div className="flex-1 overflow-y-auto">
                <Tabs defaultValue="info" className="h-full">
                  <TabsList className="w-full justify-start px-5 pt-2 bg-white rounded-none border-b gap-1">
                    <TabsTrigger value="info">Info</TabsTrigger>
                    <TabsTrigger value="payments" data-testid="car-tab-payments">Payments ({carPayments.length})</TabsTrigger>
                    <TabsTrigger value="shipping">Shipping</TabsTrigger>
                    <TabsTrigger value="docs">Docs ({selectedCar.documents?.length || 0})</TabsTrigger>
                  </TabsList>
                  <TabsContent value="info" className="p-5 space-y-4 m-0">
                    <div className="flex items-center justify-end mb-2 gap-2">
                      {editCarMode ? (
                        <>
                          <Button size="sm" variant="outline" onClick={() => setEditCarMode(false)} disabled={savingCar} data-testid="car-edit-cancel">Cancel</Button>
                          <Button size="sm" onClick={saveCarEdit} disabled={savingCar} data-testid="car-edit-save">{savingCar ? 'Saving...' : 'Save Car'}</Button>
                        </>
                      ) : (
                        <Button size="sm" variant="outline" onClick={startEditCar} data-testid="car-edit-btn">Edit Car</Button>
                      )}
                    </div>
                    {editCarMode && editCarForm ? (
                      <div className="border p-3" data-testid="car-edit-form">
                        <CarFormFields data={editCarForm} onChange={setEditCarForm} brands={brandsList} models={modelsList} ports={portsList} canSeeInternal={canSeePurchasePrice} />
                      </div>
                    ) : (
                    <>
                    <div className="border p-3 space-y-1.5"><SectionTitle>Vehicle</SectionTitle>
                      <Row label="Brand / Model" value={`${selectedCar.brand} ${selectedCar.model}`} />
                      {selectedCar.year && <Row label="Year" value={selectedCar.year} />}
                      {selectedCar.vin && <Row label="VIN" value={selectedCar.vin} mono />}
                      {selectedCar.color && <Row label="Color" value={selectedCar.color} />}
                      <Row label="Condition" value={selectedCar.condition === 'used' ? 'Used' : 'New'} />
                      {selectedCar.condition === 'used' && selectedCar.mileage_km != null && <Row label="Mileage" value={`${Number(selectedCar.mileage_km).toLocaleString()} KM`} />}
                      {selectedCar.condition === 'used' && <Row label="S-Report" value={selectedCar.s_report ? 'Yes' : 'No'} />}
                    </div>
                    <div className="border p-3 space-y-1.5"><SectionTitle>Pricing</SectionTitle>
                      {canSeeSellPrice && selectedCar.selling_price != null && <Row label="Selling Price" value={fmtDZD(selectedCar.selling_price)} />}
                      {selectedCar.deposit_amount != null && selectedCar.deposit_amount > 0 && <Row label="Deposit" value={fmtDZD(selectedCar.deposit_amount)} />}
                      {canSeeSellPrice && selectedCar.selling_price != null && <Row label="Remaining" value={fmtDZD((selectedCar.selling_price || 0) - (selectedCar.deposit_amount || 0))} />}
                      {selectedCar.cif_price != null && <Row label="CIF Shipping" value={fmtDZD(selectedCar.cif_price)} />}
                      {selectedCar.shipping_price != null && <Row label="Shipping Price" value={fmtDZD(selectedCar.shipping_price)} />}
                      {canSeePurchasePrice && selectedCar.purchase_price != null && <Row label="Purchase Price" value={`$${Number(selectedCar.purchase_price).toLocaleString()}`} />}
                    </div>
                    {(selectedCar.fuel_type || selectedCar.version_type || selectedCar.transmission || selectedCar.engine_variant || selectedCar.engine_size || selectedCar.horsepower) && (
                      <div className="border p-3 space-y-1.5"><SectionTitle>Specs</SectionTitle>
                        {selectedCar.fuel_type && <Row label="Fuel" value={selectedCar.fuel_type === 'gasoline' ? 'Gasoline' : 'Hybrid'} />}
                        {selectedCar.version_type && <Row label="Version" value={selectedCar.version_type === 'international' ? 'International' : 'Chinese'} />}
                        {selectedCar.transmission && <Row label="Transmission" value={selectedCar.transmission === 'manual' ? 'Manual' : selectedCar.transmission === 'auto_cvt' ? 'Auto CVT' : 'Auto DCT'} />}
                        {selectedCar.engine_variant && <Row label="Engine" value={selectedCar.engine_variant === 'turbo' ? 'Turbo' : 'Normal'} />}
                        {selectedCar.engine_size && <Row label="Engine Size" value={`${selectedCar.engine_size}L`} />}
                        {selectedCar.horsepower && <Row label="HP" value={`${selectedCar.horsepower} HP`} />}
                      </div>
                    )}
                    {selectedCar.extra_features?.length > 0 && (
                      <div className="border p-3"><SectionTitle>Extra Features</SectionTitle>
                        <div className="flex flex-wrap gap-1.5">
                          {selectedCar.extra_features.map(f => { const opt = EXTRA_FEATURES_OPTIONS.find(o => o.id === f); return <Badge key={f} variant="secondary" className="text-[10px]">{opt?.label || f}</Badge>; })}
                        </div>
                      </div>
                    )}
                    {selectedCar.accessories && <div className="border p-3"><SectionTitle>Accessories</SectionTitle><p className="text-sm">{selectedCar.accessories}</p></div>}
                    {selectedCar.extras_items?.length > 0 && (
                      <div className="border p-3" data-testid="car-extras-items"><SectionTitle>Extras / Accessories</SectionTitle>
                        <div className="space-y-1">
                          {selectedCar.extras_items.map((x, i) => (
                            <div key={`x-${i}-${x.name}`} className="flex items-center justify-between text-sm">
                              <span className="flex items-center gap-2">
                                {x.name}
                                <span className={`text-[10px] px-1.5 py-0.5 rounded ${x.requested_by_client ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-600'}`}>
                                  {x.requested_by_client ? 'Requested' : 'Not requested'}
                                </span>
                              </span>
                              <span className="font-mono text-xs">{fmtDZD(x.price || 0)}</span>
                            </div>
                          ))}
                        </div>
                        <div className="mt-2 pt-2 border-t flex justify-between text-xs">
                          <span className="text-muted-foreground">Added to total (requested only)</span>
                          <span className="font-bold">{fmtDZD(selectedCar.extras_items.filter(x => x.requested_by_client).reduce((s, x) => s + (parseFloat(x.price) || 0), 0))}</span>
                        </div>
                      </div>
                    )}
                    {selectedCar.spare_wheel_added && (
                      <div className="border border-amber-300 bg-amber-50 p-3" data-testid="car-spare-wheel">
                        <div className="flex items-center justify-between">
                          <p className="text-sm font-medium">Roue de secours added</p>
                          <span className="text-sm font-mono">{fmtDZD(selectedCar.spare_wheel_price || 0)}</span>
                        </div>
                        <p className="text-[11px] text-muted-foreground mt-1">Added to total · {selectedCar.brand} {selectedCar.model}</p>
                      </div>
                    )}
                    {selectedCar.custom_fees?.length > 0 && (
                      <div className="border p-3 border-amber-300 bg-amber-50/40" data-testid="car-custom-fees">
                        <SectionTitle>Custom Fees (Dédouanement / Logistique)</SectionTitle>
                        <p className="text-[10px] text-amber-700 italic mb-2">Billed separately on a Douane PO — not included in Avis de Versement / order total.</p>
                        <div className="space-y-1">
                          {selectedCar.custom_fees.map((f, i) => (
                            <div key={`fee-${i}-${f.name}`} className="flex items-center justify-between text-sm">
                              <span className="flex items-center gap-2">
                                {f.name}
                                {f.is_paid && (
                                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-green-100 text-green-700">Paid</span>
                                )}
                              </span>
                              <span className="font-mono text-xs">{fmtDZD(f.amount || 0)}</span>
                            </div>
                          ))}
                        </div>
                        <div className="mt-2 pt-2 border-t flex justify-between text-xs">
                          <span className="text-muted-foreground">Custom fees total</span>
                          <span className="font-bold">{fmtDZD(selectedCar.custom_fees.reduce((s, x) => s + (parseFloat(x.amount) || 0), 0))}</span>
                        </div>
                      </div>
                    )}
                    {selectedCar.port_id && (
                      <div className="border p-3" data-testid="car-port">
                        <SectionTitle>Port (Dédouanement)</SectionTitle>
                        <Row label="Port" value={(portsList.find(p => p.id === selectedCar.port_id)?.name) || selectedCar.port_id} />
                      </div>
                    )}
                    <div className="border p-3 space-y-1.5"><SectionTitle>End Client</SectionTitle>
                      <Row label="Name" value={selectedCar.end_client_name} />
                      <Row label="NIN" value={selectedCar.end_client_nin} mono />
                      {selectedCar.end_client_passport && <Row label="Passport" value={selectedCar.end_client_passport} mono />}
                      {selectedCar.end_client_id_card && <Row label="ID Card" value={selectedCar.end_client_id_card} mono />}
                      {selectedCar.end_client_phone && <Row label="Phone" value={selectedCar.end_client_phone} />}
                      {selectedCar.end_client_email && <Row label="Email" value={selectedCar.end_client_email} />}
                    </div>
                    <Button variant="destructive" size="sm" onClick={() => handleDeleteCar(selectedCar)} data-testid="delete-car-btn">Remove Car</Button>
                    </>
                    )}
                  </TabsContent>

                  {/* PAYMENTS */}
                  <TabsContent value="payments" className="p-5 space-y-4 m-0">
                    <div className="border p-3 space-y-2">
                      <SectionTitle>Versements</SectionTitle>
                      {(() => {
                        const selling = selectedCar.selling_price || 0;
                        const totalPaid = carPayments.reduce((s, p) => s + (p.amount || 0), 0);
                        const rem = selling - totalPaid;
                        return (
                          <>
                            <div className="flex justify-between text-sm"><span className="text-muted-foreground">Montant total</span><span className="font-bold">{fmtDZD(selling)}</span></div>
                            {carPayments.map((p) => (
                              <div key={p.id} className="flex justify-between items-center text-sm border-b pb-1" data-testid={`payment-${p.id}`}>
                                <div>
                                  <span className="font-medium">Versement {p.payment_number}</span>
                                  <span className="text-[10px] text-muted-foreground ml-2">{p.payment_method === 'ccp' ? 'CCP' : 'Espèces'} - {p.date}</span>
                                </div>
                                <div className="flex items-center gap-2">
                                  <span className="font-bold text-green-600">{fmtDZD(p.amount)}</span>
                                  <button onClick={() => handleDeletePayment(p.id)} className="text-red-400 hover:text-red-600 text-xs">x</button>
                                </div>
                              </div>
                            ))}
                            <div className="flex justify-between text-sm pt-2 border-t">
                              <span className="font-semibold">Reste à payer</span>
                              <span className={`font-bold text-base ${rem > 0 ? 'text-red-600' : 'text-green-600'}`}>{fmtDZD(rem)}</span>
                            </div>
                          </>
                        );
                      })()}
                    </div>
                    {/* Add Payment Form */}
                    <div className="border p-3 space-y-2">
                      <SectionTitle>Ajouter un versement</SectionTitle>
                      <div className="grid grid-cols-2 gap-2">
                        <div><Label className="text-xs">Montant (DA) *</Label><MoneyInput value={paymentForm.amount} onChange={(v) => setPaymentForm(prev => ({ ...prev, amount: v }))} className="h-8" data-testid="payment-amount" /></div>
                        <div><Label className="text-xs">Mode de paiement</Label>
                          <Select value={paymentForm.payment_method} onValueChange={(v) => setPaymentForm(prev => ({ ...prev, payment_method: v }))}>
                            <SelectTrigger className="h-8" data-testid="payment-method"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="especes">Espèces (Cash)</SelectItem>
                              <SelectItem value="ccp">CCP</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div><Label className="text-xs">Date</Label><Input type="date" value={paymentForm.date} onChange={(e) => setPaymentForm(prev => ({ ...prev, date: e.target.value }))} className="h-8" /></div>
                        <div><Label className="text-xs">Notes</Label><Input value={paymentForm.notes} onChange={(e) => setPaymentForm(prev => ({ ...prev, notes: e.target.value }))} className="h-8" /></div>
                      </div>
                      <Button onClick={handleAddPayment} size="sm" className="w-full" data-testid="add-payment-btn">Enregistrer le versement</Button>
                    </div>
                    {/* Avis de Versement PDF */}
                    <a href={getOrderPOPdfUrl(selectedCar.order_id, selectedCar.id)} target="_blank" rel="noopener noreferrer">
                      <Button variant="outline" className="w-full" data-testid="download-avis-pdf">Télécharger Avis de Versement (PDF)</Button>
                    </a>
                    <a href={getDeliveryNotePdfUrl(selectedCar.order_id, selectedCar.id)} target="_blank" rel="noopener noreferrer">
                      <Button variant="outline" className="w-full" data-testid="download-bon-livraison-car">Télécharger Bon de Livraison</Button>
                    </a>
                    <Button onClick={openSendEmail} className="w-full" data-testid="send-avis-email-btn">Envoyer par Email (E-Sign)</Button>
                  </TabsContent>

                  <TabsContent value="shipping" className="p-5 space-y-4 m-0">
                    <div className="border p-3 space-y-3">
                      <SectionTitle>Shipping Details</SectionTitle>
                      <div className="space-y-2">
                        <div><Label className="text-xs">Shipping Company</Label>
                          <Select value={editCarShipping.shipping_company || ''} onValueChange={(v) => setEditCarShipping({ ...editCarShipping, shipping_company: v })}>
                            <SelectTrigger className="h-9"><SelectValue placeholder="Select" /></SelectTrigger>
                            <SelectContent>{shippingCompanies.map(s => <SelectItem key={s.id} value={s.name}>{s.name}</SelectItem>)}</SelectContent>
                          </Select>
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <div><Label className="text-xs">BL Number</Label><Input value={editCarShipping.bl_number || ''} onChange={(e) => setEditCarShipping({ ...editCarShipping, bl_number: e.target.value })} className="h-9 font-mono" /></div>
                          <div><Label className="text-xs">Container #</Label><Input value={editCarShipping.container_number || ''} onChange={(e) => setEditCarShipping({ ...editCarShipping, container_number: e.target.value })} className="h-9 font-mono" /></div>
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <div><Label className="text-xs">Vessel</Label><Input value={editCarShipping.vessel_number || ''} onChange={(e) => setEditCarShipping({ ...editCarShipping, vessel_number: e.target.value })} className="h-9 font-mono" /></div>
                          <div><Label className="text-xs">ETA</Label><Input type="date" value={editCarShipping.eta || ''} onChange={(e) => setEditCarShipping({ ...editCarShipping, eta: e.target.value })} className="h-9" /></div>
                        </div>
                        <Button onClick={handleCarShippingUpdate} size="sm" className="w-full">Save Shipping Info</Button>
                      </div>
                    </div>
                  </TabsContent>
                  <TabsContent value="docs" className="p-5 m-0">
                    <div className="grid grid-cols-2 gap-2 mb-4">
                      {['passport', 'national_id', 'invoice', 'car_images', 'bl_copy'].map((cat) => (
                        <label key={cat} className="border-2 border-dashed border-border p-3 text-center cursor-pointer hover:border-primary transition">
                          <p className="text-xs font-medium capitalize">{cat.replace(/_/g, ' ')}</p>
                          <input type="file" className="hidden" onChange={(e) => handleCarFileUpload(e, cat)} accept=".pdf,.jpg,.jpeg,.png" />
                        </label>
                      ))}
                    </div>
                    {uploading && <div className="text-center py-2"><div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto" /></div>}
                    {selectedCar.documents?.length > 0 ? (
                      <div className="space-y-2">{selectedCar.documents.map((doc) => (
                        <div key={doc.id} className="flex items-center justify-between p-2 border">
                          <div className="min-w-0"><p className="text-sm font-medium truncate">{doc.original_filename}</p><p className="text-[10px] text-muted-foreground capitalize">{doc.category?.replace(/_/g, ' ')}</p></div>
                          <Badge variant="secondary" className="text-[10px] capitalize flex-shrink-0">{doc.category}</Badge>
                        </div>
                      ))}</div>
                    ) : <div className="text-center py-8 border border-dashed text-sm text-muted-foreground">No documents yet</div>}
                  </TabsContent>
                </Tabs>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>

      {/* ===== CREATE ORDER DIALOG ===== */}
      <Dialog open={createOrderOpen} onOpenChange={(open) => { if (!creatingOrder) setCreateOrderOpen(open); }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>New Order</DialogTitle></DialogHeader>
          <form onSubmit={handleCreateOrder} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Client *</Label>
                <ClientPicker
                  clients={clients}
                  value={orderForm.client_id}
                  onChange={handleClientSelect}
                  onClientCreated={(newClient) => {
                    // Optimistically insert the new client so the picker
                    // displays the name instantly, then refresh from server.
                    if (newClient?.user_id) {
                      setClients((prev) =>
                        prev.some((c) => c.user_id === newClient.user_id)
                          ? prev
                          : [newClient, ...prev]
                      );
                    }
                    loadFormData();
                  }}
                />
              </div>
              <div><Label>Supplier</Label>
                <Select value={orderForm.supplier_id} onValueChange={(v) => setOrderForm(prev => ({ ...prev, supplier_id: v }))}>
                  <SelectTrigger><SelectValue placeholder="Select supplier" /></SelectTrigger>
                  <SelectContent>{suppliers.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {agentsList.length > 0 && (
                <div><Label>Assign to Agent</Label>
                  <Select value={orderForm.assigned_agent_id} onValueChange={(v) => setOrderForm(prev => ({ ...prev, assigned_agent_id: v }))}>
                    <SelectTrigger data-testid="order-agent-select"><SelectValue placeholder="Select agent" /></SelectTrigger>
                    <SelectContent>{agentsList.map(a => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              )}
              <div><Label>Notes</Label><Textarea value={orderForm.notes} onChange={(e) => setOrderForm(prev => ({ ...prev, notes: e.target.value }))} rows={1} /></div>
            </div>

            {/* Car Lines */}
            <div className="border-t pt-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-sm">Cars ({carLines.length})</h3>
                {orderForm.client_id && (
                  <div className="flex gap-2">
                    {(!isIndividual || carLines.length === 0) && <Button type="button" variant="outline" size="sm" onClick={addCarLine} data-testid="add-car-line">+ New Car</Button>}
                    {stockVehicles.length > 0 && (!isIndividual || carLines.length === 0) && (
                      <Select onValueChange={(vId) => { const v = stockVehicles.find(sv => sv.id === vId); if (v) addFromStock(v); }}>
                        <SelectTrigger className="h-8 w-[180px] text-xs" data-testid="add-from-stock"><SelectValue placeholder="+ From Stock" /></SelectTrigger>
                        <SelectContent>
                          {stockVehicles.map(v => (
                            <SelectItem key={v.id} value={v.id}>{v.brand_name} {v.model_name} ({v.quantity})</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  </div>
                )}
              </div>

              {carLines.length === 0 && orderForm.client_id && (
                <div className="text-center py-4 border border-dashed text-sm text-muted-foreground">Add cars to this order (new or from stock)</div>
              )}

              {carLines.map((line, idx) => (
                <div key={line._lineId} className="border p-3 mb-3 bg-slate-50/50" data-testid={`car-line-${idx}`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-bold">Car {idx + 1} {line.stock_vehicle_id ? <Badge variant="secondary" className="text-[9px] ml-1">FROM STOCK</Badge> : ''}</span>
                    {!isIndividual && <button type="button" className="text-xs text-red-500 hover:underline" onClick={() => removeCarLine(idx)}>Remove</button>}
                  </div>
                  <CarFormFields data={line} onChange={(updated) => updateCarLine(idx, updated)} brands={brandsList} models={modelsList} ports={portsList} canSeeInternal={canSeePurchasePrice} />
                  <EndClientFields data={line} onChange={(updated) => updateCarLine(idx, updated)} savedEndClients={savedEndClients} isIndividual={isIndividual} />
                </div>
              ))}
            </div>

            <div className="flex justify-end gap-3 pt-2">
              <Button type="button" variant="outline" onClick={() => setCreateOrderOpen(false)}>Cancel</Button>
              <Button type="submit" data-testid="create-order-submit" disabled={carLines.length === 0 || creatingOrder}>
                {creatingOrder ? 'Creating…' : 'Create Order'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* ===== ADD CAR TO ORDER DIALOG ===== */}
      <Dialog open={addCarOpen} onOpenChange={setAddCarOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Add Car to Order</DialogTitle></DialogHeader>
          <form onSubmit={handleAddCar} className="space-y-3">
            {stockVehicles.length > 0 && (
              <div className="mb-2">
                <Label className="text-xs">Assign from Stock</Label>
                <Select onValueChange={(vId) => { const v = stockVehicles.find(sv => sv.id === vId); if (v) {
                  setCarForm(prev => ({ ...prev, stock_vehicle_id: v.id, brand: v.brand_name, model: v.model_name, year: v.year?.toString() || '', color: v.color || '', fuel_type: v.fuel_type?.toLowerCase() || '', transmission: v.transmission?.toLowerCase() || '', purchase_price: v.purchase_price?.toString() || '', selling_price: v.selling_price?.toString() || '', condition: 'new' }));
                }}}>
                  <SelectTrigger className="h-8"><SelectValue placeholder="Select stock vehicle..." /></SelectTrigger>
                  <SelectContent>{stockVehicles.map(v => <SelectItem key={v.id} value={v.id}>{v.brand_name} {v.model_name} ({v.quantity} in stock)</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            <CarFormFields data={carForm} onChange={setCarForm} brands={brandsList} models={modelsList} ports={portsList} canSeeInternal={canSeePurchasePrice} />
            <EndClientFields data={carForm} onChange={setCarForm} savedEndClients={savedEndClients} />
            <div className="flex justify-end gap-3 pt-2">
              <Button type="button" variant="outline" onClick={() => setAddCarOpen(false)}>Cancel</Button>
              <Button type="submit" data-testid="add-car-submit">Add Car</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* ===== AVIS DE VERSEMENT DIALOG ===== */}
      <Dialog open={poDialogOpen} onOpenChange={setPODialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Avis de Versement</DialogTitle></DialogHeader>
          {poData && (
            <div className="space-y-4" data-testid="po-view">
              {/* Company Header */}
              <div className="border-b-2 border-primary pb-3">
                <p className="text-lg font-bold text-primary">360 CAREXPORT</p>
                <p className="text-[11px] text-muted-foreground">Représentée par : BENLETIF Azzeddine</p>
                <p className="text-[11px] text-muted-foreground">Adresse : Cité Kanagaz Boumerdes</p>
                {poData.agent && (
                  <>
                    <p className="text-[11px] mt-1"><span className="font-semibold">Agent commerciale :</span> {poData.agent.name}</p>
                    {poData.agent.phone && <p className="text-[11px] text-muted-foreground">Tél : {poData.agent.phone}</p>}
                  </>
                )}
              </div>

              {/* Client Info */}
              {poData.client && (
                <div className="border p-3 bg-slate-50/50">
                  <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-2">Client / Donneur d'ordre</p>
                  <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
                    <Row label="Nom" value={poData.client.name} />
                    <Row label="N° Client" value={poData.order?.order_number} />
                    <Row label="N.I.N." value={poData.client.nin_number} mono />
                    <Row label="N° carte/passport" value={poData.client.passport_number || poData.client.id_card_number || '-'} mono />
                    <Row label="Téléphone" value={poData.client.phone} />
                    <Row label="E-mail" value={poData.client.email} />
                    {poData.client.address && <Row label="Adresse" value={poData.client.address} />}
                  </div>
                </div>
              )}

              {/* Cars with payments */}
              {poData.cars?.map((car, i) => (
                <div key={car.id || i} className="border p-3">
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Véhicule {poData.cars.length > 1 ? i + 1 : ''}</p>
                      <p className="text-sm font-bold">{car.brand} {car.model} {car.transmission === 'manual' ? 'MANUELLE' : car.transmission === 'auto_cvt' ? 'AUTO CVT' : car.transmission === 'auto_dct' ? 'AUTO DCT' : ''} {car.color ? car.color.toUpperCase() : ''}</p>
                      {car.accessories && <p className="text-xs text-muted-foreground">Accessoires : {car.accessories}</p>}
                      {car.vin && <p className="text-xs"><span className="text-muted-foreground">VIN :</span> <span className="font-mono font-bold bg-yellow-100 px-1">{car.vin}</span></p>}
                    </div>
                  </div>
                  <div className="border-t pt-2 mt-2 space-y-1">
                    <div className="flex justify-between text-sm"><span className="font-semibold">Montant total à payer (DA) :</span><span className="font-bold text-primary">{car.selling_price ? `${Number(car.selling_price).toLocaleString('fr-FR', {minimumFractionDigits: 2})}` : '-'}</span></div>
                    {car.payments?.map(p => (
                      <div key={p.id} className="flex justify-between text-sm">
                        <span className="text-muted-foreground">versement {p.payment_number} (DA) - {p.payment_method === 'ccp' ? 'CCP' : 'Espèces'} - {p.date} :</span>
                        <span className="font-medium">{Number(p.amount).toLocaleString('fr-FR', {minimumFractionDigits: 2})}</span>
                      </div>
                    ))}
                    <div className="flex justify-between text-sm pt-1 border-t mt-1">
                      <span className="font-bold">Reste à payer (DA) :</span>
                      <span className={`font-bold text-base ${car.remaining > 0 ? 'text-red-600' : 'text-green-600'}`}>
                        {Number(car.remaining || 0).toLocaleString('fr-FR', {minimumFractionDigits: 2})}
                      </span>
                    </div>
                  </div>
                </div>
              ))}

              {/* Total Summary */}
              <div className="border-2 border-primary p-3 bg-primary/5">
                <div className="flex justify-between text-sm"><span className="font-semibold">Total toutes voitures (DA) :</span><span className="font-bold">{fmtDZD(poData.summary?.total_selling_price)}</span></div>
                <div className="flex justify-between text-sm"><span>Total versements :</span><span className="font-bold text-green-600">{fmtDZD(poData.summary?.total_paid)}</span></div>
                <div className="flex justify-between text-sm pt-1 border-t mt-1">
                  <span className="font-bold">Reste à payer :</span>
                  <span className={`font-bold text-lg ${poData.summary?.remaining_balance > 0 ? 'text-red-600' : 'text-green-600'}`}>{fmtDZD(poData.summary?.remaining_balance)}</span>
                </div>
              </div>

              {/* Legal */}
              <p className="text-[10px] italic text-muted-foreground">Cette attestation est délivrée au besoin pour servir et valoir ce que de droit. Ce contrat annule le contrat de paiement initial.</p>

              <a href={getOrderPOPdfUrl(selectedOrder?.id)} target="_blank" rel="noopener noreferrer">
                <Button variant="outline" className="w-full mt-2" data-testid="po-download-pdf">Télécharger Avis de Versement (PDF)</Button>
              </a>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ===== OUTSTANDING BALANCE WARNING DIALOG ===== */}
      <Dialog
        open={paymentWarning.open}
        onOpenChange={(open) => { if (!open) setPaymentWarning(prev => ({ ...prev, open: false })); }}
      >
        <DialogContent className="max-w-md" data-testid="payment-warning-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-700">
              <span aria-hidden="true">⚠️</span> Outstanding Balance
            </DialogTitle>
            <DialogDescription>
              This order isn&apos;t fully paid yet. Please remind the client to complete payment before shipping.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2" data-testid="payment-warning-body">
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm">
              <div className="flex justify-between"><span className="text-gray-600">Order</span><span className="font-semibold">{paymentWarning.orderNumber}</span></div>
              <div className="flex justify-between mt-1"><span className="text-gray-600">New status</span><span className="font-semibold">{statusLabel(paymentWarning.pendingStatus)}</span></div>
            </div>
            <div className="rounded-lg border bg-white p-3 text-sm space-y-1">
              <div className="flex justify-between"><span className="text-gray-600">Total selling</span><span>{new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(paymentWarning.totalSelling || 0)} DZD</span></div>
              <div className="flex justify-between"><span className="text-gray-600">Total paid</span><span className="text-green-700">{new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(paymentWarning.totalPaid || 0)} DZD</span></div>
              <div className="flex justify-between border-t pt-1 mt-1">
                <span className="font-semibold">Outstanding</span>
                <span className="font-bold text-red-600" data-testid="payment-warning-outstanding">
                  {new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(paymentWarning.outstanding || 0)} DZD
                </span>
              </div>
            </div>
          </div>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button variant="outline" onClick={() => setPaymentWarning(prev => ({ ...prev, open: false }))} data-testid="payment-warning-cancel">
              Cancel
            </Button>
            <Button
              className="bg-amber-600 hover:bg-amber-700 text-white"
              onClick={async () => {
                const next = paymentWarning.pendingStatus;
                setPaymentWarning(prev => ({ ...prev, open: false }));
                await applyOrderStatusChange(next);
              }}
              data-testid="payment-warning-proceed"
            >
              Change status anyway
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ===== SEND EMAIL DIALOG ===== */}
      <Dialog open={sendEmailOpen} onOpenChange={setSendEmailOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Envoyer Avis de Versement par Email</DialogTitle></DialogHeader>
          <div className="space-y-4" data-testid="send-email-dialog">
            <div>
              <Label className="text-xs">Email du client *</Label>
              <Input value={emailForm.to_emails} onChange={(e) => setEmailForm(prev => ({ ...prev, to_emails: e.target.value }))} placeholder="client@email.com" data-testid="email-to" />
            </div>
            <div>
              <Label className="text-xs">Email supplémentaire (optionnel)</Label>
              <Input value={emailForm.extra_email} onChange={(e) => setEmailForm(prev => ({ ...prev, extra_email: e.target.value }))} placeholder="autre@email.com" data-testid="email-extra" />
            </div>

            {/* E-Signatures */}
            <div className="border p-3">
              <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-2">Signature Client</p>
              <div className="border border-border bg-white" data-testid="client-signature-pad">
                <SignatureCanvas ref={clientSigRef} canvasProps={{ width: 400, height: 120, className: 'w-full' }} />
              </div>
              <button type="button" onClick={() => clientSigRef.current?.clear()} className="text-xs text-red-500 mt-1 hover:underline">Effacer</button>
            </div>

            <div className="border p-3">
              <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-2">Signature Agent / Société</p>
              <div className="border border-border bg-white" data-testid="agent-signature-pad">
                <SignatureCanvas ref={agentSigRef} canvasProps={{ width: 400, height: 120, className: 'w-full' }} />
              </div>
              <button type="button" onClick={() => agentSigRef.current?.clear()} className="text-xs text-red-500 mt-1 hover:underline">Effacer</button>
            </div>

            <Button onClick={handleSendAvis} disabled={sendingEmail} className="w-full" data-testid="send-email-submit">
              {sendingEmail ? 'Envoi en cours...' : 'Envoyer avec signatures'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Order Details Dialog */}
      <Dialog open={editOrderOpen} onOpenChange={setEditOrderOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Edit Order & Client</DialogTitle></DialogHeader>
          <form onSubmit={handleSaveOrderEdit} className="space-y-4" data-testid="edit-order-form">
            <div>
              <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-2">Order</p>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs">Client</Label>
                  <Select value={editOrderForm.client_id} onValueChange={(v) => setEditOrderForm({ ...editOrderForm, client_id: v })}>
                    <SelectTrigger className="h-9" data-testid="edit-order-client"><SelectValue placeholder="Select client" /></SelectTrigger>
                    <SelectContent>{clients.map(c => <SelectItem key={c.user_id} value={c.user_id}>{c.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Status</Label>
                  <Select value={editOrderForm.status} onValueChange={(v) => setEditOrderForm({ ...editOrderForm, status: v })}>
                    <SelectTrigger className="h-9" data-testid="edit-order-status"><SelectValue placeholder="Current" /></SelectTrigger>
                    <SelectContent>{ORDER_STATUSES.map(s => <SelectItem key={s} value={s}>{statusLabel(s)}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Assigned Agent</Label>
                  <Select value={editOrderForm.assigned_agent_id} onValueChange={(v) => setEditOrderForm({ ...editOrderForm, assigned_agent_id: v })}>
                    <SelectTrigger className="h-9" data-testid="edit-order-agent"><SelectValue placeholder="Optional" /></SelectTrigger>
                    <SelectContent>{agentsList.map(a => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Supplier</Label>
                  <Select value={editOrderForm.supplier_id} onValueChange={(v) => setEditOrderForm({ ...editOrderForm, supplier_id: v })}>
                    <SelectTrigger className="h-9" data-testid="edit-order-supplier"><SelectValue placeholder="Optional" /></SelectTrigger>
                    <SelectContent>{suppliers.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="col-span-2">
                  <Label className="text-xs">Notes</Label>
                  <Input value={editOrderForm.notes}
                    onChange={(e) => setEditOrderForm({ ...editOrderForm, notes: e.target.value })}
                    className="h-9" data-testid="edit-order-notes" />
                </div>
              </div>
            </div>

            <div className="border-t pt-4">
              <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-2">Client Info (changes save to client record)</p>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs">Surname</Label>
                  <Input value={editOrderForm.client_surname}
                    onChange={(e) => setEditOrderForm({ ...editOrderForm, client_surname: e.target.value })}
                    className="h-9" data-testid="edit-client-surname" />
                </div>
                <div>
                  <Label className="text-xs">Given Name</Label>
                  <Input value={editOrderForm.client_given_name}
                    onChange={(e) => setEditOrderForm({ ...editOrderForm, client_given_name: e.target.value })}
                    className="h-9" data-testid="edit-client-given-name" />
                </div>
                <div>
                  <Label className="text-xs">Phone</Label>
                  <Input value={editOrderForm.client_phone}
                    onChange={(e) => setEditOrderForm({ ...editOrderForm, client_phone: e.target.value })}
                    className="h-9" data-testid="edit-client-phone" />
                </div>
                <div>
                  <Label className="text-xs">Email</Label>
                  <Input type="email" value={editOrderForm.client_email}
                    onChange={(e) => setEditOrderForm({ ...editOrderForm, client_email: e.target.value })}
                    className="h-9" data-testid="edit-client-email" />
                </div>
                <div className="col-span-2">
                  <Label className="text-xs">Address</Label>
                  <Input value={editOrderForm.client_address}
                    onChange={(e) => setEditOrderForm({ ...editOrderForm, client_address: e.target.value })}
                    className="h-9" data-testid="edit-client-address" />
                </div>
                <div>
                  <Label className="text-xs">NIN</Label>
                  <Input value={editOrderForm.client_nin}
                    onChange={(e) => setEditOrderForm({ ...editOrderForm, client_nin: e.target.value })}
                    className="h-9" data-testid="edit-client-nin" />
                </div>
                <div>
                  <Label className="text-xs">Passport</Label>
                  <Input value={editOrderForm.client_passport}
                    onChange={(e) => setEditOrderForm({ ...editOrderForm, client_passport: e.target.value })}
                    className="h-9" data-testid="edit-client-passport" />
                </div>
                <div>
                  <Label className="text-xs">ID Card</Label>
                  <Input value={editOrderForm.client_id_card}
                    onChange={(e) => setEditOrderForm({ ...editOrderForm, client_id_card: e.target.value })}
                    className="h-9" data-testid="edit-client-id-card" />
                </div>
                <div>
                  <Label className="text-xs">Company Name</Label>
                  <Input value={editOrderForm.client_company}
                    onChange={(e) => setEditOrderForm({ ...editOrderForm, client_company: e.target.value })}
                    className="h-9" data-testid="edit-client-company" />
                </div>
              </div>
            </div>

            <p className="text-[11px] text-muted-foreground">For car-level changes (specs, prices, extras, spare wheel, VIN, container), click the car in the list to edit it directly.</p>
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setEditOrderOpen(false)}>Cancel</Button>
              <Button type="submit" data-testid="edit-order-save">Save All</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Duplicate Orders Finder Dialog */}
      <Dialog open={duplicatesOpen} onOpenChange={(o) => !o && setDuplicatesOpen(false)}>
        <DialogContent className="max-w-4xl" data-testid="duplicates-dialog">
          <DialogHeader>
            <DialogTitle>🔍 Find duplicate orders</DialogTitle>
            <DialogDescription>
              Scans this tenant for orders with the same number OR the same client + car + day.
              Deleting a duplicate uses its internal database id, so the surviving copy is fully preserved.
            </DialogDescription>
          </DialogHeader>

          {duplicatesLoading ? (
            <p className="text-sm text-muted-foreground py-6 text-center">Scanning orders…</p>
          ) : !duplicates ? (
            <p className="text-sm text-muted-foreground py-6 text-center">No data yet.</p>
          ) : duplicates.total_groups === 0 ? (
            <div className="py-8 text-center">
              <p className="text-2xl mb-2">✅</p>
              <p className="font-semibold">No duplicates found</p>
              <p className="text-xs text-muted-foreground mt-1">
                All {duplicates.total_groups === 0 ? '' : duplicates.total_groups} order groups are clean.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="bg-amber-50 border border-amber-200 p-3 text-sm">
                Found <b>{duplicates.total_groups}</b> duplicate group{duplicates.total_groups === 1 ? '' : 's'}
                · <b>{duplicates.total_extra_docs}</b> extra doc{duplicates.total_extra_docs === 1 ? '' : 's'} can be removed.
                Each group is sorted with the recommended <b>bad twin</b> at the bottom.
              </div>
              {duplicates.groups.map((g, idx) => (
                <div key={`${g.type}-${idx}`} className="border border-border rounded">
                  <div className="bg-secondary/40 px-3 py-2 border-b border-border text-xs flex justify-between items-center">
                    <div>
                      <span className="font-mono uppercase text-[10px] bg-white border border-border px-1 py-0.5 mr-2">{g.type === 'same_order_number' ? 'SAME #' : 'SAME CAR'}</span>
                      <span className="font-medium">{g.key}</span>
                    </div>
                    <span className="text-muted-foreground">{g.count} copies</span>
                  </div>
                  <div className="divide-y divide-border">
                    {g.members.map((m) => (
                      <div key={m.mongo_id} className={`p-3 text-sm flex items-start justify-between gap-3 ${m.recommended_bad ? 'bg-red-50/50' : ''}`} data-testid={`dup-member-${m.mongo_id}`}>
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="font-mono font-bold">{m.order_number}</span>
                            {m.recommended_bad && (
                              <span className="text-[10px] bg-red-100 text-red-700 border border-red-200 px-1.5 py-0.5 rounded">RECOMMENDED · BAD TWIN</span>
                            )}
                            {!m.client_name && (
                              <span className="text-[10px] bg-amber-100 text-amber-800 border border-amber-200 px-1.5 py-0.5 rounded">no client</span>
                            )}
                            <span className="text-[11px] text-muted-foreground">{m.car_count} car{m.car_count === 1 ? '' : 's'}</span>
                          </div>
                          <p className="text-xs text-muted-foreground mt-1">
                            {m.client_name || <span className="italic">no client linked</span>}
                            {m.status ? ` · ${m.status}` : ''}
                            {m.created_at ? ` · created ${new Date(m.created_at).toLocaleString()}` : ''}
                          </p>
                          <p className="text-[10px] font-mono text-muted-foreground mt-0.5 break-all">
                            id={m.id} · mongo_id={m.mongo_id}
                          </p>
                        </div>
                        <Button
                          size="sm"
                          variant={m.recommended_bad ? 'destructive' : 'outline'}
                          disabled={deletingMongoId === m.mongo_id}
                          onClick={() => handleDeleteDuplicateTwin(m, g)}
                          data-testid={`delete-dup-${m.mongo_id}`}
                        >
                          {deletingMongoId === m.mongo_id ? 'Deleting…' : 'Delete this copy'}
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={openDuplicatesDialog} disabled={duplicatesLoading} data-testid="rescan-dups-btn">Rescan</Button>
            <Button onClick={() => setDuplicatesOpen(false)} data-testid="close-dups-btn">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import Orders Dialog */}
      <Dialog open={importOpen} onOpenChange={(o) => {
        setImportOpen(o);
        if (!o) {
          if (aiPollRef.current) { clearInterval(aiPollRef.current); aiPollRef.current = null; }
          setAiJobId(null); setAiProgress(null); setImportMode('template');
        }
      }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>Import Orders from Excel</DialogTitle></DialogHeader>
          <div className="space-y-4" data-testid="import-orders-dialog">
            {!importPreview && !importResult ? (
              <>
                <Tabs value={importMode} onValueChange={setImportMode}>
                  <TabsList className="grid grid-cols-2 w-full">
                    <TabsTrigger value="template" data-testid="tab-template-import">Template Import</TabsTrigger>
                    <TabsTrigger value="ai" data-testid="tab-ai-import">✨ Smart AI Import</TabsTrigger>
                  </TabsList>

                  {/* TEMPLATE MODE */}
                  <TabsContent value="template" className="space-y-4 mt-3">
                    <div className="p-3 bg-blue-50 border border-blue-200">
                      <p className="text-sm font-medium mb-1">Step 1: Download template</p>
                      <a href={getOrderImportTemplateUrl()} target="_blank" rel="noopener noreferrer" className="text-sm text-primary font-medium hover:underline" data-testid="download-orders-template">
                        Download Excel Template (.xlsx) →
                      </a>
                      <p className="text-xs text-muted-foreground mt-2">Fill one row per order. One client + one car per row.</p>
                    </div>
                    <div>
                      <Label>Step 2: Upload your Excel file</Label>
                      <Input
                        type="file"
                        accept=".xlsx,.xls"
                        disabled={importing}
                        data-testid="import-orders-file-input"
                        onChange={async (e) => {
                          const f = e.target.files?.[0];
                          if (!f) return;
                          setImporting(true);
                          try {
                            const fd = new FormData();
                            fd.append('file', f);
                            const res = await previewOrdersImport(fd);
                            setImportPreview(res.data);
                          } catch (err) {
                            toast.error(err.response?.data?.detail || 'Preview failed');
                          } finally { setImporting(false); }
                        }}
                      />
                      {importing && <p className="text-sm text-primary mt-2">Parsing file...</p>}
                    </div>
                  </TabsContent>

                  {/* AI MODE */}
                  <TabsContent value="ai" className="space-y-4 mt-3">
                    <div className="p-3 bg-violet-50 border border-violet-200">
                      <p className="text-sm font-medium mb-1">Smart Import — upload your existing sheet as-is</p>
                      <p className="text-xs text-muted-foreground">
                        Drop your legacy Excel (any layout, any column names, multiple tabs OK). Our AI reads each sheet, identifies the client, car, VIN, price and date automatically, and proposes a list of orders for you to review.
                      </p>
                    </div>

                    {!aiJobId && (
                      <div>
                        <Label>Upload your Excel file</Label>
                        <Input
                          type="file"
                          accept=".xlsx,.xls"
                          disabled={importing}
                          data-testid="ai-import-file-input"
                          onChange={async (e) => {
                            const f = e.target.files?.[0];
                            if (!f) return;
                            setImporting(true);
                            setAiProgress({ status: 'queued', batches_done: 0, total_batches: 0 });
                            try {
                              const fd = new FormData();
                              fd.append('file', f);
                              const res = await aiPreviewOrdersImport(fd);
                              const jobId = res.data.job_id;
                              setAiJobId(jobId);
                              // poll
                              aiPollRef.current = setInterval(async () => {
                                try {
                                  const s = await getAiImportStatus(jobId);
                                  setAiProgress(s.data);
                                  if (s.data.status === 'done') {
                                    clearInterval(aiPollRef.current); aiPollRef.current = null;
                                    setImportPreview(s.data);
                                    setImporting(false);
                                  } else if (s.data.status === 'error') {
                                    clearInterval(aiPollRef.current); aiPollRef.current = null;
                                    toast.error(s.data.detail || 'AI import failed');
                                    setImporting(false);
                                  }
                                } catch { /* keep polling */ }
                              }, 3000);
                            } catch (err) {
                              toast.error(err.response?.data?.detail || 'Upload failed');
                              setImporting(false);
                              setAiProgress(null);
                            }
                          }}
                        />
                      </div>
                    )}

                    {aiJobId && aiProgress && aiProgress.status !== 'done' && (
                      <div data-testid="ai-import-progress" className="p-3 border border-border bg-secondary/30">
                        <p className="text-sm font-medium mb-2">
                          {aiProgress.status === 'queued' && 'Queued…'}
                          {aiProgress.status === 'processing' && `AI is reading your sheets… (${aiProgress.batches_done}/${aiProgress.total_batches} batches)`}
                          {aiProgress.status === 'error' && (aiProgress.detail || 'Error')}
                        </p>
                        {aiProgress.total_batches > 0 && (
                          <div className="w-full bg-secondary h-2 rounded overflow-hidden">
                            <div
                              className="h-full bg-primary transition-all"
                              style={{ width: `${Math.round((aiProgress.batches_done / aiProgress.total_batches) * 100)}%` }}
                            />
                          </div>
                        )}
                        <p className="text-xs text-muted-foreground mt-2">This can take 30–120 seconds depending on the size of your workbook.</p>
                      </div>
                    )}
                  </TabsContent>
                </Tabs>
              </>
            ) : importPreview && !importResult ? (
              <div data-testid="import-preview">
                <p className="text-sm font-medium mb-2">Step 3: Review and confirm</p>
                <div className="grid grid-cols-3 gap-3 mb-4">
                  <div className="p-3 border border-green-200 bg-green-50 text-center">
                    <p className="text-2xl font-bold text-green-600" data-testid="preview-will-create">{importPreview.will_create}</p>
                    <p className="text-xs text-muted-foreground">Will create</p>
                  </div>
                  <div className="p-3 border border-yellow-200 bg-yellow-50 text-center">
                    <p className="text-2xl font-bold text-yellow-600">{importPreview.warning_count}</p>
                    <p className="text-xs text-muted-foreground">Warnings</p>
                  </div>
                  <div className="p-3 border border-red-200 bg-red-50 text-center">
                    <p className="text-2xl font-bold text-red-600">{importPreview.error_count}</p>
                    <p className="text-xs text-muted-foreground">Errors</p>
                  </div>
                </div>
                {importPreview.sample?.length > 0 && (
                  <div className="mb-3 max-h-48 overflow-y-auto border border-border text-xs" data-testid="import-sample-table">
                    <table className="w-full">
                      <thead className="bg-secondary sticky top-0">
                        <tr>
                          <th className="text-left p-1.5">Client</th>
                          <th className="text-left p-1.5">Car</th>
                          <th className="text-left p-1.5">VIN</th>
                          <th className="text-right p-1.5">Price</th>
                        </tr>
                      </thead>
                      <tbody>
                        {importPreview.sample.map((s, idx) => (
                          <tr key={`s-${idx}`} className="border-t border-border">
                            <td className="p-1.5">{s.client?.name}</td>
                            <td className="p-1.5">{s.car?.brand} {s.car?.model} · {s.car?.color}</td>
                            <td className="p-1.5 font-mono text-[10px]">{s.car?.vin || '—'}</td>
                            <td className="p-1.5 text-right">{(s.car?.selling_price || 0).toLocaleString()}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {importPreview.will_create > importPreview.sample.length && (
                      <p className="p-1.5 text-muted-foreground text-center">+ {importPreview.will_create - importPreview.sample.length} more…</p>
                    )}
                  </div>
                )}
                {importPreview.warnings?.length > 0 && (
                  <div className="mb-3 max-h-32 overflow-y-auto border border-border p-2 text-xs">
                    <p className="font-medium mb-1">Warnings (will import anyway):</p>
                    {importPreview.warnings.map((w, idx) => <p key={`w-${idx}-${w.slice(0, 20)}`} className="text-yellow-700">• {w}</p>)}
                  </div>
                )}
                {importPreview.errors?.length > 0 && (
                  <div className="mb-3 max-h-32 overflow-y-auto border border-border p-2 text-xs">
                    <p className="font-medium mb-1">Errors (will be skipped):</p>
                    {importPreview.errors.map((er, idx) => <p key={`e-${idx}-${er.slice(0, 20)}`} className="text-red-700">• {er}</p>)}
                  </div>
                )}
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setImportPreview(null)} className="flex-1" data-testid="import-cancel-preview">Cancel</Button>
                  <Button
                    onClick={async () => {
                      setImporting(true);
                      try {
                        const res = await confirmOrdersImport(importPreview.token);
                        setImportResult(res.data);
                        toast.success(`${res.data.created_orders} orders imported`);
                        loadData();
                      } catch (err) {
                        toast.error(err.response?.data?.detail || 'Import failed');
                      } finally { setImporting(false); }
                    }}
                    disabled={importing || importPreview.will_create === 0}
                    className="flex-1"
                    data-testid="import-confirm-btn"
                  >
                    {importing ? 'Importing...' : `Confirm Import (${importPreview.will_create})`}
                  </Button>
                </div>
              </div>
            ) : (
              <div data-testid="import-result">
                <p className="text-sm font-medium mb-2">Import complete</p>
                <div className="grid grid-cols-2 gap-2 mb-3">
                  <div className="p-3 border border-border text-center">
                    <p className="text-2xl font-bold text-green-600" data-testid="result-orders">{importResult.created_orders}</p>
                    <p className="text-xs text-muted-foreground">Orders created</p>
                  </div>
                  <div className="p-3 border border-border text-center">
                    <p className="text-2xl font-bold text-blue-600">{importResult.created_clients}</p>
                    <p className="text-xs text-muted-foreground">Clients created</p>
                  </div>
                  <div className="p-3 border border-border text-center">
                    <p className="text-2xl font-bold text-violet-600">{importResult.created_brands}</p>
                    <p className="text-xs text-muted-foreground">New brands</p>
                  </div>
                  <div className="p-3 border border-border text-center">
                    <p className="text-2xl font-bold text-violet-600">{importResult.created_models}</p>
                    <p className="text-xs text-muted-foreground">New models</p>
                  </div>
                </div>
                {importResult.errors?.length > 0 && (
                  <div className="mb-3 max-h-32 overflow-y-auto border border-border p-2 text-xs">
                    <p className="font-medium mb-1">Errors:</p>
                    {importResult.errors.map((er, idx) => <p key={`er-${idx}-${er.slice(0, 20)}`} className="text-red-700">• {er}</p>)}
                  </div>
                )}
                <Button onClick={() => { setImportOpen(false); setImportPreview(null); setImportResult(null); }} className="w-full" data-testid="import-done-btn">Done</Button>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OrdersPage;
